import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  Paper,
  useTheme,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  alpha
} from '@mui/material';
import LoadingContainer from '../../components/common/LoadingContainer';
import InlineLoader from '../../components/common/InlineLoader';
import ProgressRing from '../../components/common/ProgressRing';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Report as ReportIcon,
  BarChart as BarChartIcon,
  EmojiEvents as EmojiEventsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Whatshot as WhatshotIcon,
  LocationOn as LocationOnIcon,
  FitnessCenter as FitnessCenterIcon,
  RateReview as RateReviewIcon
} from '@mui/icons-material';
import { CompactStatsBar } from './components/CompactStatsBar';
import { SessionsPerMonthChart } from './components/SessionsPerMonthChart';
import { useAuth } from '../auth/useAuth';
import { managedLocations, whereLocation } from '../auth/managedLocations';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, Timestamp, serverTimestamp, setDoc, addDoc } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { format, differenceInWeeks, addWeeks, isBefore } from 'date-fns';
import { getClientReportSubmissions } from '../../shared/utils/reportSubmissions';
import { toDate, toISOStringSafe, formatDateSafe } from '../../shared/utils/dateHelpers';
import { latestCountedActivityByClient, SUCCESS_UPDATE_INTERVAL_DAYS } from "../../shared/success/successRecords";

// TIMEZONE HANDLING 
// When dealing with appointment timestamps like "2025-04-09T16:00:00.000Z":
// 1. We ignore the 'Z' (UTC) designation completely
// 2. We treat the date and time components as local values

// Get the date part (YYYY-MM-DD) from an ISO timestamp or date value
const getLocalDate = (dateValue: any): string => {
  try {
    if (!dateValue) return '';
    
    // If it's already a string, extract the date part
    if (typeof dateValue === 'string') {
      // Validate that it looks like a date string
      if (dateValue.length >= 10 && dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateValue.substring(0, 10); // Extract YYYY-MM-DD part
      }
    }
    
    // Otherwise, convert to date and format
    const date = toDate(dateValue);
    if (date && !isNaN(date.getTime())) {
      return format(date, 'yyyy-MM-dd');
    }
    
    return '';
  } catch (error) {
    console.error('Error getting local date:', error, dateValue);
    return '';
  }
};

// Extract hours and minutes from an ISO timestamp or date value
const getTimeComponents = (dateValue: any): { hours: number, minutes: number } => {
  if (!dateValue) {
    return { hours: 0, minutes: 0 };
  }
  
  // Convert to string if needed
  let dateString: string;
  if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    const date = toDate(dateValue);
    if (!date) {
      return { hours: 0, minutes: 0 };
    }
    dateString = date.toISOString();
  }
  
  // Match pattern to extract hours and minutes, regardless of timezone
  const match = dateString.match(/T(\d{2}):(\d{2})/);
  if (!match) {
    console.warn('Invalid time format:', dateString);
    return { hours: 0, minutes: 0 };
  }
  
  const [_, hoursStr, minutesStr] = match;
  const hours = parseInt(hoursStr);
  const minutes = parseInt(minutesStr);
  
  return { hours, minutes };
};

// Check if a date value is for today (comparing only YYYY-MM-DD part)
const isToday = (dateValue: any): boolean => {
  try {
    if (!dateValue) return false;
    
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const dateStr = getLocalDate(dateValue);
    
    // If we couldn't get a valid date string, return false
    if (!dateStr) return false;
    
    return dateStr === todayStr;
  } catch (error) {
    console.error('Error checking if date is today:', error, dateValue);
    return false;
  }
};

// Check if a date value is for tomorrow (comparing only YYYY-MM-DD part)
const isTomorrow = (dateValue: any): boolean => {
  try {
    if (!dateValue) return false;
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    const dateStr = getLocalDate(dateValue);
    
    // If we couldn't get a valid date string, return false
    if (!dateStr) return false;
    
    return dateStr === tomorrowStr;
  } catch (error) {
    console.error('Error checking if date is tomorrow:', error, dateValue);
    return false;
  }
};

// Format the time part of an ISO timestamp or date value (e.g., "4:00 pm")
const formatTime = (dateValue: any): string => {
  try {
    const { hours, minutes } = getTimeComponents(dateValue);
    
    // Validate hours and minutes
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.warn('Invalid time components:', { hours, minutes, dateValue });
      return 'N/A';
    }
    
    // Create a date object just to format the time
    const timeDate = new Date();
    timeDate.setHours(hours);
    timeDate.setMinutes(minutes);
    timeDate.setSeconds(0);
    
    // Check if the date is valid
    if (isNaN(timeDate.getTime())) {
      console.warn('Invalid date created from time components:', { hours, minutes, dateValue });
      return 'N/A';
    }
    
    // Use 12-hour format with am/pm
    return format(timeDate, 'h:mm a');
  } catch (error) {
    console.error('Error formatting time:', error, dateValue);
    return 'N/A';
  }
};

// Convert time to minutes since midnight for sorting
const getMinutesSinceMidnight = (dateValue: any): number => {
  try {
    const { hours, minutes } = getTimeComponents(dateValue);
    
    // Validate hours and minutes
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.warn('Invalid time components for sorting:', { hours, minutes, dateValue });
      return 0; // Return 0 for invalid times (will sort to beginning)
    }
    
    return hours * 60 + minutes;
  } catch (error) {
    console.error('Error calculating minutes since midnight:', error, dateValue);
    return 0;
  }
};

// Helper function to safely format dates of different types
const formatSafeDate = (date: any, formatString: string): string => {
  return formatDateSafe(date, formatString, 'Invalid date');
};

interface FirestoreClientData {
  id: string;
  client_name?: string;
  client_id?: string;
  email?: string;
  active_programme?: boolean;
  visit_history?: {
    session_date: string;
    service_type: string;
    service_category: string;
    staff_id?: string;
    staff_name?: string;
    pricing_option?: string;
  }[];
  visit_dates?: string[];
  pending_checkin?: boolean;
  first_session_date?: string;
  first_ignition_session_date?: string;
  primary_location?: string;
  lead_coach_id?: string;
  lead_coach_name?: string;
  collaborator_ids?: string[];
  collaborator_names?: string[];
  conversion_status?: boolean;
  nps_rating?: number;
  ignition_expiration_date?: string;
  current_programme?: string;
  physical_performance_screenings?: number;
  nutrition_sessions?: number;
  metabolic_screenings?: number;
  pt_sessions?: number;
  pending_report?: boolean;
  pending_report_initial?: boolean;
  pending_report_review?: boolean;
  report_type?: 'Initial' | 'Review';
  report_status?: string;
  // Foundation programme fields
  first_foundation_session_date?: string;
  foundation_expiration_date?: string;
  foundation_allied_health_sessions?: number;
  foundation_physical_performance_sessions?: number;
  foundation_nutrition_sessions?: number;
  foundation_pt_sessions?: number;
  foundation_pilates_sessions?: number;
  needs_success_update?: boolean;
  last_success_update?: string;
  awaiting_first_success_update?: boolean;
  has_alerts?: boolean;
  alert_reasons?: string[];
  cycle_count?: number;
  upcoming_appointments?: {
    coach: string;
    coach_id: number;
    location_id: number;
    location: string;
    date: {
      value: string;
    };
    program_id?: number | string;
    session_type_id?: number;
  }[];
  [key: string]: any; // Allow for additional properties
}

interface Client extends FirestoreClientData {
  pending_check_in_type?: '2_week' | '10_week';
  foundation_has_alerts?: boolean;
  foundation_alert_reasons?: string[];
  foundation_pending_report_initial?: boolean;
  foundation_pending_report_review?: boolean;
}


interface TerminatedSubscription {
  id: string;
  client_id: string;
  client_full_name: string;
  termination_date: Timestamp;
  contract_name: string;
  staff_id: string;
  last_updated: Timestamp;
}

interface DashboardProps {
  userRole: string;
  userMetadata?: { coach_id?: string; assigned_location?: string };
}

interface SessionCountData {
  month: string;
  count: number;
}

interface StaffSessionData {
  staff_id: string;
  staff_name: string;
  formatted_staff_name?: string;
  session_count: SessionCountData[];
  primary_location?: string;
}

/**
 * The original dashboard, as live on 27 Oct 2026 (25cfc2a), with managers'
 * several locations added. Everyone sees it unless an admin opts them in to
 * Today and Actions (see DashboardRoute). Delete it when those ship to everyone.
 */
const LegacyDashboard: React.FC<DashboardProps> = ({ userRole: propUserRole, userMetadata: propUserMetadata }) => {
  const theme = useTheme();
  const { userRole = propUserRole, userMetadata = propUserMetadata, user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Refs for sections to scroll to
  const clientAlertsRef = useRef<HTMLDivElement>(null);
  const foundationAlertsRef = useRef<HTMLDivElement>(null);
  const pendingCheckinsRef = useRef<HTMLDivElement>(null);
  const pendingReportsRef = useRef<HTMLDivElement>(null);
  const successUpdatesRef = useRef<HTMLDivElement>(null);
  const upcomingSessionsRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    inactiveClients: 0,
    renewedClients: 0,
    nonRenewedClients: 0,
    pendingReports: 0,
    pendingCheckins: 0,
    outstandingSuccessUpdates: 0,
    upcomingSessions: 0,
    completedSessions: 0,
    npsAverage: 0,
    conversionRate: 0,
    attritionRate: 0,
    firstCycleConversionRate: 0
  });
  const [recentClients, setRecentClients] = useState<Client[]>([]);
  const [clientsWithAlerts, setClientsWithAlerts] = useState<Client[]>([]);
  const [clientsWithFoundationAlerts, setClientsWithFoundationAlerts] = useState<Client[]>([]);
  const [clientsWithPendingCheckins, setClientsWithPendingCheckins] = useState<Client[]>([]);
  const [clientsWithRecentCollaboratorVisits, setClientsWithRecentCollaboratorVisits] = useState<Client[]>([]);
  const [clientsWithRecentLeadCoachVisits, setClientsWithRecentLeadCoachVisits] = useState<Client[]>([]);
  const [clientsReadyForAssessments, setClientsReadyForAssessments] = useState<Client[]>([]);
  const [clientsNeedingSuccessUpdates, setClientsNeedingSuccessUpdates] = useState<Client[]>([]);
  const [clientsWithUpcomingSessions, setClientsWithUpcomingSessions] = useState<Client[]>([]);
  const [clientsWithPendingReports, setClientsWithPendingReports] = useState<Client[]>([]);
  const [clientsWithUpcomingExpirations, setClientsWithUpcomingExpirations] = useState<Client[]>([]);
  const [recentCancellations, setRecentCancellations] = useState<TerminatedSubscription[]>([]);
  const navigate = useNavigate();
  const [isSuccessUpdatesExpanded, setIsSuccessUpdatesExpanded] = useState(false);
  const [isUpcomingSessionsExpanded, setIsUpcomingSessionsExpanded] = useState(false);
  const [isPendingReportsExpanded, setIsPendingReportsExpanded] = useState(false);
  const [isCancellationsExpanded, setIsCancellationsExpanded] = useState(false);
  const [isPendingCheckinsExpanded, setIsPendingCheckinsExpanded] = useState(false);
  const [isRecentSessionsExpanded, setIsRecentSessionsExpanded] = useState(false);
  const [isClientAlertsExpanded, setIsClientAlertsExpanded] = useState(false);
  const [isFoundationAlertsExpanded, setIsFoundationAlertsExpanded] = useState(false);
  const [isUpcomingExpirationsExpanded, setIsUpcomingExpirationsExpanded] = useState(false);
  
  // Clear alert dialog state
  const [clearAlertDialogOpen, setClearAlertDialogOpen] = useState(false);
  const [selectedClientForClear, setSelectedClientForClear] = useState<Client | null>(null);
  const [selectedAlertReasons, setSelectedAlertReasons] = useState<string[]>([]);
  const [clearAlertReason, setClearAlertReason] = useState('');
  const [clearingAlert, setClearingAlert] = useState(false);
  const [foundationClearAlertDialogOpen, setFoundationClearAlertDialogOpen] = useState(false);
  const [selectedFoundationClientForClear, setSelectedFoundationClientForClear] = useState<Client | null>(null);
  const [selectedFoundationAlertReasons, setSelectedFoundationAlertReasons] = useState<string[]>([]);
  const [clearFoundationAlertReason, setClearFoundationAlertReason] = useState('');
  const [clearingFoundationAlert, setClearingFoundationAlert] = useState(false);

  // Dashboard check-in shortcut dialog state
  const [dashboardCheckInDialogOpen, setDashboardCheckInDialogOpen] = useState(false);
  const [selectedDashboardCheckInClient, setSelectedDashboardCheckInClient] = useState<Client | null>(null);
  const [dashboardCheckInType, setDashboardCheckInType] = useState<'2_week' | '10_week'>('2_week');
  const [dashboardCheckInNotes, setDashboardCheckInNotes] = useState('');
  const [dashboardCheckInError, setDashboardCheckInError] = useState('');
  const [isSubmittingDashboardCheckIn, setIsSubmittingDashboardCheckIn] = useState(false);
  
  // Session count chart state
  const [staffSessionData, setStaffSessionData] = useState<StaffSessionData[]>([]);
  const [isLoadingSessionData, setIsLoadingSessionData] = useState(false);
  
  // Refresh trigger for reloading data after clearing alerts
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Scroll to section function
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        let clients: Client[] = [];
        
        // Different queries based on user role
        let clientQuery;
        
        if (userRole === 'ADMIN') {
          // Admins can see all clients
          clientQuery = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details')
          );
        } else if (userRole === 'MANAGER' && managedLocations(userMetadata).length > 0) {
          // Managers can see clients at each of their locations
          clientQuery = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            whereLocation('primary_location', managedLocations(userMetadata))
          );
        } else if (userRole === 'USER' && userMetadata?.coach_id) {
          // Coaches can only see their clients where they are the lead coach
          console.log('USER coach_id:', userMetadata.coach_id, 'type:', typeof userMetadata.coach_id);
          
          // Try with string coach_id first
          if (typeof userMetadata.coach_id === 'string') {
            clientQuery = query(
              collection(db, 'bigquery_cache/client_data/comprehensive_details'),
              where('lead_coach_id', '==', userMetadata.coach_id)
            );
            
            // Execute query to check if we get results
            try {
              const initialSnapshot = await getDocs(clientQuery);
              
              // If no results, try with numeric coach_id
              if (initialSnapshot.size === 0 && !isNaN(Number(userMetadata.coach_id))) {
                const numericCoachId = Number(userMetadata.coach_id);
                console.log('Trying with numeric coach_id:', numericCoachId);
                clientQuery = query(
                  collection(db, 'bigquery_cache/client_data/comprehensive_details'),
                  where('lead_coach_id', '==', numericCoachId)
                );
              }
            } catch (queryError) {
              console.error('Error with initial USER query:', queryError);
              // Try fallback query without where clause
              clientQuery = query(
                collection(db, 'bigquery_cache/client_data/comprehensive_details')
              );
            }
          } 
          // If coach_id is not a string, try with the value as is
          else {
            clientQuery = query(
              collection(db, 'bigquery_cache/client_data/comprehensive_details'),
              where('lead_coach_id', '==', userMetadata.coach_id)
            );
          }
        } else {
          // No valid role/metadata to build a safe query.
          console.warn('Dashboard: skipping client fetch due to missing role metadata', { userRole, userMetadata });
          setStats({
            totalClients: 0,
            activeClients: 0,
            inactiveClients: 0,
            renewedClients: 0,
            nonRenewedClients: 0,
            pendingReports: 0,
            pendingCheckins: 0,
            outstandingSuccessUpdates: 0,
            upcomingSessions: 0,
            completedSessions: 0,
            npsAverage: 0,
            conversionRate: 0,
            attritionRate: 0,
            firstCycleConversionRate: 0
          });
          setRecentClients([]);
          setClientsWithAlerts([]);
          setClientsWithFoundationAlerts([]);
          setClientsWithPendingCheckins([]);
          setClientsWithRecentCollaboratorVisits([]);
          setClientsWithRecentLeadCoachVisits([]);
          setClientsReadyForAssessments([]);
          setClientsNeedingSuccessUpdates([]);
          setClientsWithUpcomingSessions([]);
          setClientsWithPendingReports([]);
          setClientsWithUpcomingExpirations([]);
          setRecentCancellations([]);
          return;
        }
        
        // Execute query
        let clientSnapshot;
        try {
          clientSnapshot = await getDocs(clientQuery);
          console.log('Client query successful, found:', clientSnapshot.size, 'clients');
        } catch (error) {
          console.error('Error fetching clients:', error);
          throw error;
        }
        
        // Fetch all check-ins to check completion status
        let checkInsSnapshot;
        const completedCheckIns = new Map();
        
        try {
          // For USER role, only fetch check-ins for their clients
          if (userRole === 'USER' && clientSnapshot.size > 0) {
            const clientIds = clientSnapshot.docs.map(doc => doc.id);
            // Batch queries in groups of 10 due to Firestore limitations
            const batches = [];
            for (let i = 0; i < clientIds.length; i += 10) {
              const batch = clientIds.slice(i, i + 10);
              const batchQuery = query(
                collection(db, 'client_check_ins'),
                where('client_id', 'in', batch)
              );
              batches.push(getDocs(batchQuery));
            }
            
            const results = await Promise.all(batches);
            checkInsSnapshot = {
              docs: results.flatMap(result => result.docs),
              size: results.reduce((sum, result) => sum + result.size, 0)
            };
          } else {
            // For ADMIN and MANAGER, fetch all check-ins
            checkInsSnapshot = await getDocs(collection(db, 'client_check_ins'));
          }
          
          console.log('Check-ins query successful, found:', checkInsSnapshot.size, 'check-ins');
          
          checkInsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.client_id && data.check_in_type) {
              const key = `${data.client_id}_${data.check_in_type}`;
              completedCheckIns.set(key, true);
            }
          });
        } catch (error) {
          console.error('Error fetching check-ins:', error);
          // Continue without check-ins data
        }
        
        // Fetch all client success records to determine when updates are needed
        let clientSuccessSnapshot;
        let latestSuccessUpdates = new Map<string, Date>();
        
        try {
          // For USER role, only fetch success records for their clients
          if (userRole === 'USER' && clientSnapshot.size > 0) {
            // Use doc.id to match the original implementation
            const clientIds = clientSnapshot.docs.map(doc => doc.id);
            // Batch queries in groups of 10 due to Firestore limitations
            const batches = [];
            for (let i = 0; i < clientIds.length; i += 10) {
              const batch = clientIds.slice(i, i + 10);
              const batchQuery = query(
                collection(db, 'client_success'),
                where('client_id', 'in', batch)
              );
              batches.push(getDocs(batchQuery));
            }
            
            const results = await Promise.all(batches);
            clientSuccessSnapshot = {
              docs: results.flatMap(result => result.docs),
              size: results.reduce((sum, result) => sum + result.size, 0)
            };
          } else {
            // For ADMIN and MANAGER, fetch all success records
            clientSuccessSnapshot = await getDocs(collection(db, 'client_success'));
          }
          
          console.log('Client success query successful, found:', clientSuccessSnapshot?.size || 0, 'records');
        } catch (error) {
          console.error('Error fetching client success:', error);
          // Continue without client success data
        }
        
        // Process client success data to find the latest real update for each client
        // (blank auto-created placeholders and blank assessment seeds are ignored)
        if (clientSuccessSnapshot) {
          latestSuccessUpdates = latestCountedActivityByClient(
            clientSuccessSnapshot.docs.map(d => ({ id: d.id, data: d.data() }))
          );
        }
        
        // Process client data
        const clientsData = clientSnapshot.docs.map(doc => {
          const data = doc.data() as FirestoreClientData;
          const rawClientId = data.client_id ?? doc.id;
          const clientId = String(rawClientId);
          return {
            ...data,
            client_id: clientId,
            id: doc.id
          };
        });
        
        // Fetch all client reports to check which ones have been generated
        const reportsPromises = clientsData.map(async (client) => {
          if (!client.client_id) return { clientId: '', initialReportExists: false, reviewReportExists: false };
          
          try {
            // Check for generated reports in clients/{client_id}/reports collection
            const reportsRef = collection(db, `clients/${client.client_id}/reports`);
            const reportsSnapshot = await getDocs(reportsRef);
            
            let initialReportExists = false;
            let reviewReportExists = false;
            let foundationInitialReportExists = false;
            let foundationReviewReportExists = false;
            
            reportsSnapshot.docs.forEach(doc => {
              const reportData = doc.data();
              // Check both report_type field and latest_report.report_type
              // Reports can have report_type at root level or nested in latest_report object
              const reportType = reportData.report_type || reportData.latest_report?.report_type;
              
              if (reportType === 'Initial') {
                initialReportExists = true;
              } else if (reportType === 'Review') {
                reviewReportExists = true;
              } else if (reportType === 'Foundation_Initial') {
                foundationInitialReportExists = true;
              } else if (reportType === 'Foundation_Review') {
                foundationReviewReportExists = true;
              }
            });
            
            return {
              clientId: client.client_id,
              initialReportExists,
              reviewReportExists,
              foundationInitialReportExists,
              foundationReviewReportExists
            };
          } catch (error) {
            console.error(`Error fetching reports for client ${client.client_id}:`, error);
            return {
              clientId: client.client_id,
              initialReportExists: false,
              reviewReportExists: false,
              foundationInitialReportExists: false,
              foundationReviewReportExists: false
            };
          }
        });
        
        const reportsResults = await Promise.all(reportsPromises);
        const reportsMap = new Map();
        
        reportsResults.forEach(result => {
          if (result.clientId) {
            reportsMap.set(result.clientId, {
              initialReportExists: result.initialReportExists,
              reviewReportExists: result.reviewReportExists,
              foundationInitialReportExists: result.foundationInitialReportExists,
              foundationReviewReportExists: result.foundationReviewReportExists
            });
          }
        });
        
        // Calculate pending check-ins and success updates
        const clientsWithCheckInAndSuccessStatus = await Promise.all(
          clientsData.map(async (client: FirestoreClientData) => {
            // Determine first session date
            let firstSessionDate: Date | null = null;
            
            // If first_session_date is directly available, use it
            if (client.first_session_date) {
              firstSessionDate = toDate(client.first_session_date);
            } 
            // Otherwise, find it from visit_dates
            else if (client.visit_dates && client.visit_dates.length > 0) {
              // Convert all dates and filter out invalid ones
              const validDates = client.visit_dates
                .map(d => toDate(d))
                .filter((d): d is Date => d !== null)
                .sort((a, b) => a.getTime() - b.getTime());
              
              if (validDates.length > 0) {
                firstSessionDate = validDates[0];
              }
            }
            
            // Check if client needs a success update (6+ weeks since last update)
            let needsSuccessUpdate = false;
            let lastSuccessUpdate: string | undefined = undefined;
            
            const sixWeeksAgo = new Date();
            sixWeeksAgo.setDate(sixWeeksAgo.getDate() - SUCCESS_UPDATE_INTERVAL_DAYS); // 6 weeks
            
            const latestSuccessDate = latestSuccessUpdates.get(client.client_id || '');
            if (latestSuccessDate) {
              lastSuccessUpdate = toISOStringSafe(latestSuccessDate);
              needsSuccessUpdate = latestSuccessDate < sixWeeksAgo && client.active_programme === true;
            } else if (firstSessionDate && client.active_programme === true) {
              // If no success update exists and client has had sessions for over 6 weeks
              needsSuccessUpdate = firstSessionDate < sixWeeksAgo;
            }
            
            // If we have a first session date, check for pending check-ins
            if (firstSessionDate && client.active_programme) {
              const now = new Date();
              const twoWeekCheckInDate = addWeeks(firstSessionDate, 2);
              const tenWeekCheckInDate = addWeeks(firstSessionDate, 10);
              
              // Check if 2-week check-in is pending
              const isTwoWeekCheckInDue = isBefore(twoWeekCheckInDate, now);
              const isTwoWeekCheckInCompleted = completedCheckIns.has(`${client.client_id}_2_week`);
              
              // Check if 10-week check-in is pending
              const isTenWeekCheckInDue = isBefore(tenWeekCheckInDate, now);
              const isTenWeekCheckInCompleted = completedCheckIns.has(`${client.client_id}_10_week`);
              
              // Determine pending check-in type
              let pendingCheckInType: '2_week' | '10_week' | undefined = undefined;
              let hasPendingCheckIn = false;
              
              // Check if firstSessionDate is after March 1, 2025
              const cutoffDate = new Date(2025, 3, 1); // Month is 0-indexed, so 2 = March
              const isAfterCutoffDate = firstSessionDate > cutoffDate;
              
              if (isAfterCutoffDate && isTwoWeekCheckInDue && !isTwoWeekCheckInCompleted) {
                pendingCheckInType = '2_week';
                hasPendingCheckIn = true;
              } else if (isAfterCutoffDate && isTenWeekCheckInDue && !isTenWeekCheckInCompleted) {
                pendingCheckInType = '10_week';
                hasPendingCheckIn = true;
              }
              
              return {
                ...client,
                first_session_date: firstSessionDate ? toISOStringSafe(firstSessionDate) : undefined,
                pending_checkin: hasPendingCheckIn,
                pending_check_in_type: pendingCheckInType,
                needs_success_update: needsSuccessUpdate,
                last_success_update: lastSuccessUpdate,
                awaiting_first_success_update: needsSuccessUpdate && !lastSuccessUpdate
              };
            }
            
            return {
              ...client,
              needs_success_update: needsSuccessUpdate,
              last_success_update: lastSuccessUpdate,
              awaiting_first_success_update: needsSuccessUpdate && !lastSuccessUpdate
            };
          })
        );
        
        // Process the clients to check for missing assessments
        // Use Promise.all with map to handle the async report submission checks
        const clientsWithReportChecks = await Promise.all(
          clientsWithCheckInAndSuccessStatus.map(async (client) => {
            // Check for client alerts based on conditions and missing report submissions
            const hasAlerts = await checkClientAlerts(client);
            
            // Format names for display
            const formattedClientName = formatName(client.client_name);
            const formattedLeadCoachName = formatName(client.lead_coach_name);
            const formattedCollaboratorNames = Array.isArray(client.collaborator_names)
              ? client.collaborator_names.map(name => formatName(name))
              : [];
            
            // Check if client reports have been generated
            const reports = reportsMap.get(client.client_id || '');
            
            // Real-time check for pending reports based on actual conditions
            // This approach matches the Ignition alerts logic and doesn't rely on BigQuery cache flags
            let hasPendingInitialReport = false;
            let hasPendingReviewReport = false;
            
            // Check if client is in Ignition 2025 program
            const programName = client.current_programme?.toLowerCase() || '';
            const isIgnition2025 = programName.includes('ignition') && programName.includes('2025');
            
            if (isIgnition2025 && client.first_ignition_session_date) {
              const firstSessionDate = toDate(client.first_ignition_session_date);
              if (firstSessionDate) {
                
                // Fetch report submissions to check assessment completion
                const clientId = client.client_id || client.id;
                let reportSubmissions: any[] = [];
                if (clientId) {
                  try {
                    reportSubmissions = await getClientReportSubmissions(clientId);
                  } catch (error) {
                    console.error(`Error fetching report submissions for pending reports check:`, error);
                  }
                }
                
                const submittedCategories = reportSubmissions.map(report => report.uploadCategory);
                
                // Helper function to check if all required assessments are submitted
                // Disregard session counts - only check if ALL assessments are submitted
                const hasAllInitialAssessments = () => {
                  // All Initial assessments must be submitted
                  const requiredInitialAssessments = [
                    "Nutrition Plan",
                    "Metabolic Initial Assessment",
                    "Movement Initial Assessment",
                    "InBody Scan",
                    "Training Plan"
                  ];
                  
                  // Check if all required assessments are submitted
                  return requiredInitialAssessments.every(assessment => 
                    submittedCategories.includes(assessment)
                  );
                };
                
                const hasAllReviewAssessments = () => {
                  // All Review assessments must be submitted
                  const requiredReviewAssessments = [
                    "Nutrition Review",
                    "Metabolic Review Assessment",
                    "Movement Review Assessment",
                    "Training Review"
                  ];
                  
                  // Check core review assessments
                  const hasAllCoreReview = requiredReviewAssessments.every(assessment => 
                    submittedCategories.includes(assessment)
                  );
                  
                  // Also check for InBody scan with review context
                  const hasReviewInBody = reportSubmissions.some(report => 
                    report.uploadCategory === "InBody Scan" && report.report_type === "Review"
                  );
                  
                  return hasAllCoreReview && hasReviewInBody;
                };
                
                // Initial report is pending if all assessments are complete but report not generated
                if (!reports?.initialReportExists && hasAllInitialAssessments()) {
                  // Also check report_status field as a secondary validation
                  if (client.report_status === undefined || 
                      client.report_status === null || 
                      client.report_status === '' || 
                      (typeof client.report_status === 'string' && 
                        !client.report_status.includes('Initial') && 
                        !client.report_status.includes('Review'))) {
                    hasPendingInitialReport = true;
                  }
                }
                
                // Review report is pending if all assessments are complete but report not generated
                if (!reports?.reviewReportExists && hasAllReviewAssessments()) {
                  // Only if initial report exists or status shows it's completed
                  if (reports?.initialReportExists || 
                      (typeof client.report_status === 'string' && client.report_status.includes('Initial'))) {
                    hasPendingReviewReport = true;
                  }
                }
              }
            }
            
            // Return the enhanced client object
            return {
              ...client,
              client_name: formattedClientName,
              lead_coach_name: formattedLeadCoachName,
              collaborator_names: formattedCollaboratorNames,
              has_alerts: hasAlerts.hasAlerts,
              alert_reasons: hasAlerts.reasons,
              pending_report_initial: hasPendingInitialReport,
              pending_report_review: hasPendingReviewReport,
              // For backward compatibility
              pending_report: hasPendingInitialReport || hasPendingReviewReport
            } as Client;
          })
        );
        
        // Set the processed clients as our clients array
        clients = clientsWithReportChecks;

        // Apply Foundation alert calculations
        const clientsWithFoundationChecks = await Promise.all(
          clients.map(async (client: FirestoreClientData) => {
            return checkFoundationAlerts(client, reportsMap);
          })
        );
        
        clients = clientsWithFoundationChecks;
        
        let totalClients = clients.length;
        let activeCount = 0;
        let inactiveCount = 0;
        let renewedCount = 0;
        let nonRenewedCount = 0;
        let pendingReports = 0;
        let pendingCheckins = 0;
        let outstandingSuccessUpdates = 0;
        let sessionCount = 0;
        let npsSum = 0;
        let npsCount = 0;
        let upcomingSessionsCount = 0;
        let convertedCycleCount = 0; // For First Cycle Conversion

        // Filter clients for different sections
        const clientsWithAlerts: Client[] = [];
        const clientsWithFoundationAlerts: Client[] = [];
        const clientsWithPendingCheckins: Client[] = [];
        const clientsWithRecentCollaboratorVisits: Client[] = [];
        const clientsWithRecentLeadCoachVisits: Client[] = [];
        const clientsReadyForAssessments: Client[] = [];
        const clientsNeedingSuccessUpdates: Client[] = [];
        const clientsWithUpcomingSessions: Client[] = [];
        const clientsWithPendingReports: Client[] = [];
        const clientsWithUpcomingExpirations: Client[] = [];
        
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        clients.forEach(client => {
          // Count active/inactive clients
          if (client.active_programme) {
            activeCount++;
          } else {
            inactiveCount++;
          }
          
          // Count renewed/non-renewed clients
          if (client.conversion_status) {
            renewedCount++;
          } else {
            nonRenewedCount++;
          }
          
          // Count pending reports (Ignition + Foundation)
          const hasPendingIgnitionReport = client.pending_report_initial || client.pending_report_review;
          const hasPendingFoundationReport = client.foundation_pending_report_initial || client.foundation_pending_report_review;

          if (hasPendingIgnitionReport || hasPendingFoundationReport) {
            pendingReports++;
            clientsWithPendingReports.push(client);
          }
          
          // Count pending check-ins
          if (client.pending_checkin) {
            pendingCheckins++;
            clientsWithPendingCheckins.push(client);
          }
          
          // Count outstanding success updates (including those awaiting their first one)
          if (client.needs_success_update) {
            outstandingSuccessUpdates++;
            clientsNeedingSuccessUpdates.push(client);
          }
          
          
          // Count completed sessions
          if (client.visit_dates && Array.isArray(client.visit_dates)) {
            sessionCount += client.visit_dates.length;
          }
          
          // Calculate NPS average
          if (client.nps_rating !== undefined && client.nps_rating !== null) {
            npsSum += client.nps_rating;
            npsCount++;
          }
          
          // Check for alerts
          if (client.has_alerts) {
            clientsWithAlerts.push(client);
          }
          
          if (client.foundation_has_alerts) {
            clientsWithFoundationAlerts.push(client as Client);
          }
          
          // Check for upcoming ignition expirations (within 2 weeks)
          if (client.ignition_expiration_date && 
              client.current_programme?.toLowerCase().includes('ignition') && 
              client.current_programme?.toLowerCase().includes('2025')) {
            try {
              const expirationDate = toDate(client.ignition_expiration_date);
              if (expirationDate) {
                const now = new Date();
                const daysUntilExpiration = Math.floor((expirationDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
              
              // Calculate total and remaining PT sessions
              let totalSessions = 0;
              let usedSessions = 0;
              
              // Extract total sessions from current_programme
              if (client.current_programme) {
                if (client.current_programme.toLowerCase().includes('promo')) {
                  // Promo packages have 3 sessions
                  totalSessions = 4;
                } else {
                  // Extract number before "PT" (e.g., "Ignition Programme | 20 PT" → 20)
                  const match = client.current_programme.match(/(\d+)\s*PT/);
                  if (match && match[1]) {
                    totalSessions = parseInt(match[1], 10);
                  }
                }
              }
              
              // Count used sessions from visit_history where pricing_option matches current_programme
              if (client.visit_history && Array.isArray(client.visit_history) && client.current_programme) {
                usedSessions = client.visit_history.filter(visit => 
                  visit.pricing_option === client.current_programme
                ).length;
              }
              
              // Calculate remaining sessions
              const remainingSessions = Math.max(0, totalSessions - usedSessions);
              
              // If expiration is within 14 days (2 weeks)
              if (daysUntilExpiration <= 14 && daysUntilExpiration > 0) {
                // Add expiration info and session counts to the client object
                const clientWithExpiration = {
                  ...client,
                  days_until_expiration: daysUntilExpiration,
                  total_sessions: totalSessions,
                  used_sessions: usedSessions,
                  remaining_sessions: remainingSessions
                };
                clientsWithUpcomingExpirations.push(clientWithExpiration);
              }
            }
            } catch (error) {
              console.error('Error calculating expiration date for client:', client.client_id, error);
            }
          }
          
          // Check for recent collaborator visits
          if (client.visit_history && Array.isArray(client.visit_history)) {
            const recentVisits = client.visit_history.filter(visit => {
              const visitDate = toDate(visit.session_date);
              return visitDate && visitDate >= oneWeekAgo && 
                     client.collaborator_ids && 
                     visit.staff_id && 
                     client.collaborator_ids.includes(visit.staff_id);
            });
            
            if (recentVisits.length > 0) {
              clientsWithRecentCollaboratorVisits.push(client);
            }
            
            // Check for recent lead coach visits
            const recentLeadCoachVisits = client.visit_history.filter(visit => {
              const visitDate = toDate(visit.session_date);
              return visitDate && visitDate >= oneWeekAgo && 
                     visit.staff_id && 
                     client.lead_coach_id && 
                     visit.staff_id === client.lead_coach_id;
            });
            
            if (recentLeadCoachVisits.length > 0) {
              clientsWithRecentLeadCoachVisits.push(client);
            }
          }
          
          // Check if client is ready for assessments
          if (client.ready_for_assessment) {
            clientsReadyForAssessments.push(client);
          }
          
          // Check for upcoming sessions (today and tomorrow)
          let upcomingSessions = 0;
          
          if (client.upcoming_appointments && Array.isArray(client.upcoming_appointments)) {
            // Get today and tomorrow's date
            // Get today and tomorrow dates as strings for comparison
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
            
            // Filter appointments based on user role
            const relevantAppointments = client.upcoming_appointments.filter(appointment => {
              // Extract the date part (YYYY-MM-DD) from the appointment timestamp
              // Handle both appointment.date.value and appointment.date formats
              const appointmentDateValue = appointment.date?.value || appointment.date;
              const appointmentDateStr = getLocalDate(appointmentDateValue);
              
              // Check if appointment is today or tomorrow using string comparison
              const isToday = appointmentDateStr === todayStr;
              const isTomorrow = appointmentDateStr === tomorrowStr;
              const isRelevantDate = isToday || isTomorrow;
              
              if (!isRelevantDate) return false;
              
              // For USER role, only count sessions where they are the coach
              if (userRole === 'USER' && userMetadata?.coach_id) {
                return appointment.coach_id === Number(userMetadata.coach_id);
              }
              
              // For MANAGER role, only count sessions at their locations
              const locations = managedLocations(userMetadata);
              if (userRole === 'MANAGER' && locations.length > 0) {
                // Use includes() instead of exact match to handle different location formats
                // This checks if either string contains the other, ignoring case
                const appointmentLoc = appointment.location?.toLowerCase() || '';
                return locations.some((location) => {
                  const assignedLoc = location.toLowerCase();
                  return appointmentLoc.includes(assignedLoc) || assignedLoc.includes(appointmentLoc);
                });
              }
              
              // For ADMIN role, count all sessions
              return true;
            });
            
            if (relevantAppointments.length > 0) {
              upcomingSessions += relevantAppointments.length;
              
              // Add each appointment as a separate entry
              relevantAppointments.forEach(appointment => {
                clientsWithUpcomingSessions.push({
                  ...client,
                  // Only include the current appointment in the array
                  upcoming_appointments: [appointment]
                });
              });
            }
          }
          
          upcomingSessionsCount += upcomingSessions;
          
          // Check for cycle count > 1 for First Cycle Conversion
          if (client.cycle_count !== undefined && client.cycle_count > 1) {
            convertedCycleCount++;
          }
        });
        
        const npsAverage = npsCount > 0 ? npsSum / npsCount : 0;
        const conversionRate = totalClients > 0 ? (renewedCount / totalClients) * 100 : 0;
        const attritionRate = totalClients > 0 ? (nonRenewedCount / totalClients) * 100 : 0;
        const firstCycleConversionRate = totalClients > 0 ? (convertedCycleCount / totalClients) * 100 : 0;
        
        // Sort clients by most recent visit
        const sortedClients = [...clients].sort((a, b) => {
          const aDate = a.visit_dates && a.visit_dates.length > 0 ? 
            toDate(a.visit_dates[a.visit_dates.length - 1]) : new Date(0);
          const bDate = b.visit_dates && b.visit_dates.length > 0 ? 
            toDate(b.visit_dates[b.visit_dates.length - 1]) : new Date(0);
          // Handle null dates
          if (!aDate) return 1;
          if (!bDate) return -1;
          return bDate.getTime() - aDate.getTime();
        });
        
        // Sort upcoming expirations by days_until_expiration (ascending) and remaining_sessions (descending)
        const sortedUpcomingExpirations = [...clientsWithUpcomingExpirations].sort((a, b) => {
          // First sort by days until expiration (ascending - most urgent first)
          if (a.days_until_expiration !== b.days_until_expiration) {
            return a.days_until_expiration - b.days_until_expiration;
          }
          // Then sort by remaining sessions (descending - most sessions first)
          return (b.remaining_sessions || 0) - (a.remaining_sessions || 0);
        });
        
        // Sort clients with alerts - prioritize those with report alerts
        const sortedClientsWithAlerts = clientsWithAlerts.sort((a, b) => {
          const aHasReportAlert = a.alert_reasons?.some(r => r.toLowerCase().includes('report')) || false;
          const bHasReportAlert = b.alert_reasons?.some(r => r.toLowerCase().includes('report')) || false;
          
          // Clients with report alerts come first
          if (aHasReportAlert && !bHasReportAlert) return -1;
          if (!aHasReportAlert && bHasReportAlert) return 1;
          
          // If both have or don't have report alerts, maintain original order
          return 0;
        });

        const sortedFoundationAlerts = clientsWithFoundationAlerts.sort((a, b) => {
          const aHasReportAlert = a.foundation_alert_reasons?.some(r => r.toLowerCase().includes('report')) || false;
          const bHasReportAlert = b.foundation_alert_reasons?.some(r => r.toLowerCase().includes('report')) || false;

          if (aHasReportAlert && !bHasReportAlert) return -1;
          if (!aHasReportAlert && bHasReportAlert) return 1;
          return 0;
        });
        
        // Update state with all the data
        setRecentClients(sortedClients);
        setClientsWithAlerts(sortedClientsWithAlerts);
        setClientsWithFoundationAlerts(sortedFoundationAlerts);
        setClientsWithPendingCheckins(clientsWithPendingCheckins);
        setClientsWithRecentCollaboratorVisits(clientsWithRecentCollaboratorVisits.slice(0, 5));
        setClientsWithRecentLeadCoachVisits(clientsWithRecentLeadCoachVisits.slice(0, 5));
        setClientsReadyForAssessments(clientsReadyForAssessments.slice(0, 5));
        setClientsNeedingSuccessUpdates(clientsNeedingSuccessUpdates);
        setClientsWithUpcomingSessions(clientsWithUpcomingSessions);
        setClientsWithPendingReports(clientsWithPendingReports);
        setClientsWithUpcomingExpirations(sortedUpcomingExpirations);
        
        // Fetch recent subscription cancellations
        try {
          let terminatedSubscriptionsQuery;
          
          if (userRole === 'ADMIN') {
            // Admins can see all terminated subscriptions
            terminatedSubscriptionsQuery = query(
              collection(db, 'terminated_subscriptions'),
              orderBy('last_updated', 'desc'),
              limit(5)
            );
          } else if (userRole === 'MANAGER' && managedLocations(userMetadata).length > 0) {
            // For future implementation: filter by location if needed
            terminatedSubscriptionsQuery = query(
              collection(db, 'terminated_subscriptions'),
              orderBy('last_updated', 'desc'),
              limit(5)
            );
          } else if (userRole === 'USER' && userMetadata?.coach_id) {
            // Coaches can only see terminations where they are the staff
            console.log('Creating terminated subscriptions query for USER with staff_id:', userMetadata.coach_id);
            // Try with numeric coach_id since staff_id in terminated_subscriptions is likely numeric
            const staffId = !isNaN(Number(userMetadata.coach_id)) ? Number(userMetadata.coach_id) : userMetadata.coach_id;
            terminatedSubscriptionsQuery = query(
              collection(db, 'terminated_subscriptions'),
              where('staff_id', '==', staffId),
              orderBy('last_updated', 'desc'),
              limit(5)
            );
          }
          
          if (terminatedSubscriptionsQuery) {
            console.log('Executing terminated subscriptions query...');
            const terminatedSnapshot = await getDocs(terminatedSubscriptionsQuery);
            console.log('Terminated subscriptions query successful, found:', terminatedSnapshot.size, 'records');
            const terminatedSubscriptions: TerminatedSubscription[] = [];
            
            terminatedSnapshot.forEach((doc) => {
              terminatedSubscriptions.push({
                id: doc.id,
                ...doc.data(),
              } as TerminatedSubscription);
            });
            
            setRecentCancellations(terminatedSubscriptions);
          }
        } catch (error: any) {
          console.error('Error fetching terminated subscriptions:', error);
          console.error('Terminated subscriptions error details:', {
            code: error?.code,
            message: error?.message
          });
        }
        
        // Log success update calculation details
        console.log('Dashboard - Success Update Calculation:', {
          totalClients: clients.length,
          activeClients: clients.filter(c => c.active_programme === true).length,
          clientsWithNeedsSuccessUpdate: clients.filter(c => c.needs_success_update === true).length,
          clientsWithLastSuccessUpdate: clients.filter(c => c.last_success_update).length,
          clientsWithBothFlags: clients.filter(c => c.needs_success_update && c.last_success_update).length,
          finalOutstandingCount: outstandingSuccessUpdates
        });
        
        setStats({
          totalClients,
          activeClients: activeCount,
          inactiveClients: inactiveCount,
          renewedClients: renewedCount,
          nonRenewedClients: nonRenewedCount,
          pendingReports,
          pendingCheckins,
          outstandingSuccessUpdates,
          upcomingSessions: upcomingSessionsCount,
          completedSessions: sessionCount,
          npsAverage,
          conversionRate,
          attritionRate,
          firstCycleConversionRate
        });
        
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error details:', {
          code: error?.code,
          message: error?.message,
          stack: error?.stack
        });
      } finally {
        setLoading(false);
      }
    };
    
    const fetchStaffSessionCounts = async () => {
      try {
        setIsLoadingSessionData(true);
        let staffSessionsRef;
        
        if (userRole === 'ADMIN') {
          // Admins see all staff
          staffSessionsRef = collection(db, 'staffLocations');
        } else if (userRole === 'MANAGER' && managedLocations(userMetadata).length > 0) {
          // Managers see staff at their locations
          // First get all staff
          const allStaffRef = collection(db, 'staffLocations');
          const allStaffSnapshot = await getDocs(allStaffRef);
          const locations = managedLocations(userMetadata).map((location) => location.toLowerCase());
          
          // Then filter by location
          const filteredByLocation = allStaffSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.primary_location && 
                   locations.some((location) => data.primary_location.toLowerCase().includes(location));
          });
          
          if (filteredByLocation.length === 0) {
            setStaffSessionData([]);
            setIsLoadingSessionData(false);
            return;
          }
          
          // Process each staff member's data
          const staffData: StaffSessionData[] = [];
          
          for (const doc of filteredByLocation) {
            const data = doc.data() as StaffSessionData;
            if (data.session_count && Array.isArray(data.session_count)) {
              staffData.push({
                staff_id: data.staff_id,
                staff_name: data.formatted_staff_name || data.staff_name,
                session_count: data.session_count,
                primary_location: data.primary_location
              });
            }
          }
          
          setStaffSessionData(staffData);
          setIsLoadingSessionData(false);
          return;
        } else if (userRole === 'USER' && userMetadata?.coach_id) {
          // Users only see their own data
          const staffId = userMetadata.coach_id;
          const staffDoc = doc(db, 'staffLocations', staffId);
          
          try {
            const staffSnapshot = await getDoc(staffDoc);
            
            if (staffSnapshot.exists()) {
              const data = staffSnapshot.data() as StaffSessionData;
              if (data.session_count && Array.isArray(data.session_count)) {
                setStaffSessionData([{
                staff_id: staffId,
                staff_name: data.formatted_staff_name || data.staff_name,
                session_count: data.session_count,
                primary_location: data.primary_location
              }]);
              } else {
                setStaffSessionData([]);
              }
            } else {
              setStaffSessionData([]);
            }
          } catch (error) {
            console.error('Error fetching staff session data:', error);
            setStaffSessionData([]);
          }
          
          setIsLoadingSessionData(false);
          return;
        }
        
        // If we get here, we're fetching all staff for ADMIN role
        if (staffSessionsRef) {
          const staffSnapshot = await getDocs(staffSessionsRef);
          const staffData: StaffSessionData[] = [];
          
          staffSnapshot.forEach(doc => {
            const data = doc.data() as StaffSessionData;
            if (data.session_count && Array.isArray(data.session_count)) {
              staffData.push({
                staff_id: data.staff_id,
                staff_name: data.formatted_staff_name || data.staff_name,
                session_count: data.session_count,
                primary_location: data.primary_location
              });
            }
          });
          
          setStaffSessionData(staffData);
        }
      } catch (error) {
        console.error('Error fetching staff session counts:', error);
      } finally {
        setIsLoadingSessionData(false);
      }
    };

    fetchDashboardData();
    fetchStaffSessionCounts();
    // Log dashboard access for audit
    const logDashboardAccess = async () => {
      try {
        const auditUserId = user?.uid || (userMetadata && 'uid' in userMetadata ? userMetadata.uid : undefined);
        const auditUserName = userMetadata && 'displayName' in userMetadata ? userMetadata.displayName : undefined;
        const auditUserEmail = userMetadata && 'email' in userMetadata ? userMetadata.email : undefined;
        console.log('[Audit Log Debug]', { user, userMetadata, auditUserId });
        if (!auditUserId) {
          console.warn('Dashboard audit log skipped: No uid available');
          return;
        }
        // Throttle: Only log once every 3 hours per user
        const logKey = `dashboard_log_last_${auditUserId}`;
        const lastLog = localStorage.getItem(logKey);
        const now = Date.now();
        if (lastLog && now - parseInt(lastLog, 10) < 3 * 60 * 60 * 1000) {
          // Less than 3 hours since last log
          console.log('Dashboard audit log skipped: Throttle in effect');
          return;
        }
        await setDoc(doc(db, 'audit_logs', `dashboard_${now}`), {
          action: 'Open Dashboard',
          actionType: 'ACCESS',
          userId: auditUserId,
          userName: auditUserName || auditUserEmail || 'Unknown',
          userRole: userRole || 'Unknown',
          timestamp: serverTimestamp(),
          details: 'Dashboard Accessed'
        });
        // Update last log time
        localStorage.setItem(logKey, now.toString());
      } catch (err) {
        console.error('Failed to log dashboard access:', err);
      }
    };
    logDashboardAccess();
  }, [userRole, userMetadata, refreshTrigger]);

 

  // Helper function to check for client alerts based on specified conditions
  const checkClientAlerts = async (client: FirestoreClientData): Promise<{ hasAlerts: boolean, reasons: string[] }> => {
    const reasons: string[] = [];
    
    if (!client.ignition_expiration_date) {
      return { hasAlerts: false, reasons };
    }

    // Check if programme contains both "ignition" AND "2025"
    const programName = client.current_programme?.toLowerCase() || '';
    if(!programName.includes('ignition') || !programName.includes('2025')) {
      return { hasAlerts: false, reasons };
    }
    
    const clearedReasons: string[] = [];
    try {
      // Check for cleared alerts for this client
      const clearedAlertsRef = collection(db, 'ignition_alerts_cleared');
      const clearedAlertsQuery = query(clearedAlertsRef, where('clientId', '==', client.client_id || client.id));
      const clearedAlertsSnapshot = await getDocs(clearedAlertsQuery);

      clearedAlertsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.alertReasons && Array.isArray(data.alertReasons)) {
          clearedReasons.push(...data.alertReasons);
        }
      });
    } catch (error) {
      console.error('Error fetching cleared Ignition alerts:', error);
    }
    
    // Helper function to check if a reason has been cleared
    const isReasonCleared = (reason: string): boolean => {
      return clearedReasons.includes(reason);
    };
    
    const expirationDate = toDate(client.ignition_expiration_date);
    if (!expirationDate) {
      return { hasAlerts: false, reasons };
    }
    
    const now = new Date();
    const weeksUntilExpiration = Math.round((expirationDate.getTime() - now.getTime()) / (7 * 24 * 60 * 60 * 1000));
    
    // Add null check for first_session_date
    let weeksSinceFirstSession = 0;
    if (client.first_ignition_session_date) {
      const firstSessionDate = toDate(client.first_ignition_session_date);
      if (firstSessionDate) {
        weeksSinceFirstSession = Math.floor((now.getTime() - firstSessionDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      }
    }
    
    // Get redeemed session counts from the correct Firestore fields
    const nutritionSessions = client.nutrition_sessions || 0;
    const metabolicSessions = client.metabolic_screenings || 0;
    const performanceSessions = client.physical_performance_screenings || 0;

    // If any of these conditions are true less than 10 weeks before expiration
    if (weeksSinceFirstSession >=2 && weeksUntilExpiration > 0) {
      if (nutritionSessions === 0) {
        const reason = '0 nutrition sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
      if (metabolicSessions === 0) {
        const reason = '0 metabolic sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
      if (performanceSessions === 0) {
        const reason = '0 performance screening sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      } 
      
      // Check if Initial report is missing
      const clientId = client.client_id || client.id;
      if (clientId) {
        try {
          // Check if there's a document in the reports collection
          const reportsRef = collection(db, 'clients', clientId, 'reports');
          const reportsQuery = query(reportsRef);
          const reportsSnapshot = await getDocs(reportsQuery);
          
          // Check if any document has Initial report type
          let hasInitialReport = false;
          reportsSnapshot.docs.forEach(doc => {
            const reportData = doc.data();
            const reportType = reportData.report_type || reportData.latest_report?.report_type;
            if (reportType === 'Initial') {
              hasInitialReport = true;
            }
          });
          
          if (!hasInitialReport && (
              client.report_status === undefined || 
              client.report_status === null || 
              client.report_status === '' || 
              (typeof client.report_status === 'string'  && 
                !client.report_status.includes('Initial') && 
                !client.report_status.includes('Review')))) {
            const reason = 'Initial report not generated [At 2 weeks]';
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        } catch (error) {
          console.error('Error checking report documents:', error);
          // Fall back to the original check if there's an error
          if (client.report_status === undefined || 
              client.report_status === null || 
              client.report_status === '' || 
              (typeof client.report_status === 'string'  && 
                !client.report_status.includes('Initial') && 
                !client.report_status.includes('Review'))) {
            const reason = 'Initial report not generated [At 2 weeks]';
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        }
      }
    }
    
    // If any of these conditions are true less than 4 weeks before expiration
    if (weeksSinceFirstSession >= 6 && weeksUntilExpiration > 0) {
      if (nutritionSessions <= 1) {
        const reason = `${nutritionSessions} nutrition sessions redeemed [Less than 6 weeks remaining]`;
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
    }
    
    // If any of these conditions are true on or after the expiration date
    if (weeksUntilExpiration <= 0) {
      if (nutritionSessions <= 2) {
        const reason = `${nutritionSessions} nutrition sessions redeemed [Less than 1 week remaining]`;
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
      if (metabolicSessions <= 1) {
        const reason = `${metabolicSessions} metabolic sessions redeemed [Less than 1 week remaining]`;
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
      if (performanceSessions <= 1) {
        const reason = `${performanceSessions} performance screening sessions redeemed [Less than 1 week remaining]`;
        if (!isReasonCleared(reason)) {
          reasons.push(reason);
        }
      }
      // Check if Review report is missing
      const clientId = client.client_id || client.id;
      if (clientId) {
        try {
          // Check if there's a document in the reports collection with report_type = 'Review'
          const reportsRef = collection(db, 'clients', clientId, 'reports');
          const reviewReportsQuery = query(reportsRef, where('report_type', '==', 'Review'));
          const reviewReportsSnapshot = await getDocs(reviewReportsQuery);
          
          const hasReviewReport = !reviewReportsSnapshot.empty;
          
          if (!hasReviewReport && (
              client.report_status === undefined || 
              client.report_status === null || 
              client.report_status === '' || 
              (typeof client.report_status === 'string' && 
              !client.report_status.includes('Review')))) {
            const reason = 'Review report not generated [Less than 1 week remaining]';
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        } catch (error) {
          console.error('Error checking review report documents:', error);
          // Fall back to the original check if there's an error
          if (client.report_status === undefined || 
              client.report_status === null || 
              client.report_status === '' || 
              (typeof client.report_status === 'string' && 
              !client.report_status.includes('Review'))) {
            const reason = 'Review report not generated [Less than 1 week remaining]';
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        }
      }
    }
    
    // ======= NEW REPORT SUBMISSION CHECK LOGIC =======
    const clientId = client.client_id || client.id;
    if (clientId) {
      try {
        // Get all the report submissions for this client
        const reportSubmissions = await getClientReportSubmissions(clientId);
        
        // Check which assessment types have been submitted
        const submittedCategories = reportSubmissions.map(report => report.uploadCategory);
        
        // Define expected categories
        const initialCategories = [
          "Training Plan", 
          "Metabolic Initial Assessment", 
          "Movement Initial Assessment", 
          "InBody Scan", 
          "Nutrition Plan"
        ];
        
        const reviewCategories = [
          "Nutrition Review", 
          "Metabolic Review Assessment", 
          "Movement Review Assessment",
          "Training Review",
          "InBody Scan"
        ];
        
        // Check initial assessments
        if (nutritionSessions > 0 && !submittedCategories.includes("Nutrition Plan")) {
          const reason = "Nutrition Initial Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        if (metabolicSessions > 0 && !submittedCategories.includes("Metabolic Initial Assessment")) {
          const reason = "Metabolic Initial Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        if (performanceSessions > 0 && !submittedCategories.includes("Movement Initial Assessment")) {
          const reason = "Physical Performance Initial Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        // If core assessments are submitted, check for supporting assessments
        const hasInitialNutrition = submittedCategories.includes("Nutrition Plan");
        const hasInitialMetabolic = submittedCategories.includes("Metabolic Initial Assessment");
        const hasInitialMovement = submittedCategories.includes("Movement Initial Assessment");
        
        if (hasInitialNutrition && hasInitialMetabolic && hasInitialMovement) {
          if (!submittedCategories.includes("InBody Scan")) {
            const reason = "InBody Initial Assessment Submission Outstanding";
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
          
          if (!submittedCategories.includes("Training Plan")) {
            const reason = "Training Plan Initial Submission Outstanding";
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        }
        
        // Check review assessments
        if (nutritionSessions > 2 && !submittedCategories.includes("Nutrition Review")) {
          const reason = "Nutrition Review Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        if (metabolicSessions > 1 && !submittedCategories.includes("Metabolic Review Assessment")) {
          const reason = "Metabolic Review Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        if (performanceSessions > 1 && !submittedCategories.includes("Movement Review Assessment")) {
          const reason = "Physical Performance Review Assessment Submission Outstanding";
          if (!isReasonCleared(reason)) {
            reasons.push(reason);
          }
        }
        
        // If core review assessments are submitted, check for supporting assessments
        const hasReviewNutrition = submittedCategories.includes("Nutrition Review");
        const hasReviewMetabolic = submittedCategories.includes("Metabolic Review Assessment");
        const hasReviewMovement = submittedCategories.includes("Movement Review Assessment");
        
        if (hasReviewNutrition && hasReviewMetabolic && hasReviewMovement) {
          // Check for InBody scan with review context
          const hasReviewInBody = reportSubmissions.some(report => 
            report.uploadCategory === "InBody Scan" && report.report_type === "Review"
          );
          
          if (!hasReviewInBody) {
            const reason = "InBody Review Assessment Submission Outstanding";
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
          
          if (!submittedCategories.includes("Training Review")) {
            const reason = "Training Review Assessment Submission Outstanding";
            if (!isReasonCleared(reason)) {
              reasons.push(reason);
            }
          }
        }
      } catch (error) {
        console.error(`Error checking report submissions for client ${clientId}:`, error);
      }
    }
    
    return { hasAlerts: reasons.length > 0, reasons };
  };

  // Foundation alerts logic (mirrors Ignition pattern with Foundation-specific rules)
  const checkFoundationAlerts = async (
    client: FirestoreClientData,
    reportsMap: Map<string, any>
  ): Promise<Client> => {
    const updatedClient: Client = {
      ...client,
      foundation_has_alerts: false,
      foundation_alert_reasons: [],
    };

    // Require foundation dates
    if (!client.first_foundation_session_date || !client.foundation_expiration_date) {
      return updatedClient;
    }

    const firstFoundationSession = toDate(client.first_foundation_session_date);
    const foundationExpiration = toDate(client.foundation_expiration_date);
    if (!firstFoundationSession || !foundationExpiration) {
      return updatedClient;
    }

    const clearedReasons: string[] = [];
    try {
      // Check cleared alerts for this client
      const clearedAlertsRef = collection(db, 'foundation_alerts_cleared');
      const clearedAlertsQuery = query(clearedAlertsRef, where('clientId', '==', client.client_id || client.id));
      const clearedAlertsSnapshot = await getDocs(clearedAlertsQuery);

      clearedAlertsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.alertReasons && Array.isArray(data.alertReasons)) {
          clearedReasons.push(...data.alertReasons);
        }
      });
    } catch (error) {
      console.error('Error fetching cleared Foundation alerts:', error);
    }
    const isReasonCleared = (reason: string) => clearedReasons.includes(reason);

    const now = new Date();
    const daysSinceStart = Math.floor((now.getTime() - firstFoundationSession.getTime()) / (1000 * 60 * 60 * 24));
    const daysUntilExpiration = Math.floor((foundationExpiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Pull report existence from earlier fetch
    const reports = reportsMap.get(client.client_id || '') || {};
    const foundationInitialReportExists = reports.foundationInitialReportExists || false;
    const foundationReviewReportExists = reports.foundationReviewReportExists || false;

    // Fetch submissions once to compute pending flags and assessment completeness
    const clientId = client.client_id || client.id;
    let reportSubmissions: any[] = [];
    if (clientId) {
      try {
        reportSubmissions = await getClientReportSubmissions(clientId);
      } catch (error) {
        console.error(`Error fetching report submissions for Foundation alerts:`, error);
      }
    }

    // Helpers to filter submissions by stage
    const isInitialStage = (submission: any) =>
      submission.formData?.['Assessment Stage (Foundation)'] === 'Initial';
    const isReviewStage = (submission: any) =>
      submission.formData?.['Assessment Stage (Foundation)'] === 'Review';

    const submissionHasCategory = (category: string, stageCheck: (s: any) => boolean) =>
      reportSubmissions.some(
        (submission) => stageCheck(submission) && submission.uploadCategory === category
      );

    // Pending flags based on required submissions
    const hasInitialNutrition = submissionHasCategory('Nutrition Plan', isInitialStage);
    const hasInitialTraining = submissionHasCategory('Training Plan', isInitialStage);
    const hasInitialInbody = submissionHasCategory('InBody Scan', isInitialStage);
    const hasInitialMovement = submissionHasCategory('Movement Initial Assessment', isInitialStage);
    const hasInitialAlliedHealth = submissionHasCategory('Allied Health Assessment', isInitialStage);

    const foundationPendingInitial =
      hasInitialNutrition &&
      hasInitialTraining &&
      hasInitialInbody &&
      hasInitialMovement &&
      hasInitialAlliedHealth &&
      !foundationInitialReportExists;

    const hasReviewNutrition = submissionHasCategory('Nutrition Review', isReviewStage);
    const hasReviewTraining = submissionHasCategory('Training Review', isReviewStage);
    const hasReviewInbody = submissionHasCategory('InBody Scan', isReviewStage);
    const hasReviewMovement = submissionHasCategory('Movement Review Assessment', isReviewStage);

    const foundationPendingReview =
      hasReviewNutrition &&
      hasReviewTraining &&
      hasReviewInbody &&
      hasReviewMovement &&
      !foundationReviewReportExists;

    const reasons: string[] = [];

    // At 2 weeks since first foundation session (before expiration)
    if (daysSinceStart >= 14 && foundationExpiration > now) {
      if ((client.foundation_allied_health_sessions || 0) === 0) {
        const reason = '0 allied health sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
      if ((client.foundation_physical_performance_sessions || 0) === 0) {
        const reason = '0 physical performance (Vald) sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
      if ((client.foundation_nutrition_sessions || 0) === 0) {
        const reason = '0 nutrition sessions redeemed [At 2 weeks]';
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
      if (!foundationInitialReportExists) {
        const reason = 'Initial report not generated [At 2 weeks]';
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
    }

    // Less than 1 week remaining (persists after expiration)
    if (daysUntilExpiration <= 7) {
      if ((client.foundation_physical_performance_sessions || 0) < 2) {
        const reason = `${client.foundation_physical_performance_sessions || 0} physical performance (Vald) sessions redeemed [Less than 1 week remaining]`;
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
      if ((client.foundation_nutrition_sessions || 0) < 2) {
        const reason = `${client.foundation_nutrition_sessions || 0} nutrition sessions redeemed [Less than 1 week remaining]`;
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
      if (!foundationReviewReportExists) {
        const reason = 'Review report not generated [Less than 1 week remaining]';
        if (!isReasonCleared(reason)) reasons.push(reason);
      }
    }

    // Assessment submission outstanding reasons (Initial)
    if (hasInitialNutrition && !hasInitialAlliedHealth) {
      const reason = 'Allied Health Initial Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }
    if (hasInitialNutrition && !hasInitialInbody) {
      const reason = 'Inbody Initial Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }
    if (hasInitialNutrition && !hasInitialMovement) {
      const reason = 'Movement Initial Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }
    if (hasInitialNutrition && !hasInitialTraining) {
      const reason = 'Training Plan Initial Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }

    // Assessment submission outstanding reasons (Review)
    if (hasReviewNutrition && !hasReviewInbody) {
      const reason = 'Inbody Review Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }
    if (hasReviewNutrition && !hasReviewMovement) {
      const reason = 'Movement Review Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }
    if (hasReviewNutrition && !hasReviewTraining) {
      const reason = 'Training Review Assessment Submission Outstanding';
      if (!isReasonCleared(reason)) reasons.push(reason);
    }

    updatedClient.foundation_alert_reasons = reasons;
    updatedClient.foundation_has_alerts = reasons.length > 0;
    updatedClient.foundation_pending_report_initial = foundationPendingInitial;
    updatedClient.foundation_pending_report_review = foundationPendingReview;

    return updatedClient;
  };

  // Helper function to format names from "Lastname, Firstname" to "Firstname Lastname" with proper case
  const formatName = (name?: string): string => {
    if (!name) return '';
    
    // Split by comma and trim whitespace
    const parts = name.split(',').map(part => part.trim());
    
    if (parts.length !== 2) {
      // If not in "Lastname, Firstname" format, just capitalize each word
      return name.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // Get lastname and firstname
    const [lastName, firstName] = parts;
    
    // Capitalize each part of the name
    const formattedFirstName = firstName.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    const formattedLastName = lastName.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Return in "Firstname Lastname" format
    return `${formattedFirstName} ${formattedLastName}`;
  };

  // Handle client card click to navigate to client details
  const handleClientClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const getCheckInTypeLabel = (type: '2_week' | '10_week') => (
    type === '2_week' ? '2-Week' : '10-Week'
  );

  const resetDashboardCheckInDialog = () => {
    setDashboardCheckInDialogOpen(false);
    setSelectedDashboardCheckInClient(null);
    setDashboardCheckInType('2_week');
    setDashboardCheckInNotes('');
    setDashboardCheckInError('');
  };

  const executeDashboardCheckInWrite = async <T,>(
    operation: () => Promise<T>,
    operationName: string,
    maxAttempts = 3
  ): Promise<T> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.info(`Dashboard check-in: ${operationName} attempt ${attempt}/${maxAttempts}`);
        return await operation();
      } catch (error) {
        lastError = error;

        if (attempt >= maxAttempts) {
          break;
        }

        console.warn(`Dashboard check-in: ${operationName} attempt ${attempt} failed; retrying`, error);
        await new Promise(resolve => setTimeout(resolve, attempt * 500));
      }
    }

    console.error(`Dashboard check-in: ${operationName} failed after ${maxAttempts} attempts`, lastError);
    throw lastError;
  };

  const openDashboardCheckInDialog = (client: Client, checkInType?: '2_week' | '10_week') => {
    const resolvedCheckInType = checkInType || client.pending_check_in_type;

    if (!resolvedCheckInType) {
      console.warn('Dashboard check-in: cannot open dialog without pending check-in type', {
        clientId: client.client_id,
        documentId: client.id
      });
      return;
    }

    console.info('Dashboard check-in: opening shortcut dialog', {
      clientId: client.client_id,
      checkInType: resolvedCheckInType
    });

    setSelectedDashboardCheckInClient(client);
    setDashboardCheckInType(resolvedCheckInType);
    setDashboardCheckInNotes('');
    setDashboardCheckInError('');
    setDashboardCheckInDialogOpen(true);
  };

  const handleDashboardCheckInSubmit = async () => {
    if (!selectedDashboardCheckInClient || !dashboardCheckInNotes.trim()) {
      return;
    }

    const clientId = selectedDashboardCheckInClient.client_id;
    if (!clientId) {
      console.error('Dashboard check-in: selected client is missing client_id', selectedDashboardCheckInClient);
      setDashboardCheckInError('This client is missing a client ID. Open the client profile and try again.');
      return;
    }

    setIsSubmittingDashboardCheckIn(true);
    setDashboardCheckInError('');

    const checkInData = {
      client_id: clientId,
      check_in_type: dashboardCheckInType,
      response: dashboardCheckInNotes.trim(),
      submitted_by: user?.uid || '',
      submitted_by_name: user?.displayName && user.displayName.trim() !== ''
        ? user.displayName
        : user?.email || 'Unknown User',
      submitted_at: serverTimestamp()
    };

    try {
      console.info('Dashboard check-in: submitting shortcut check-in', {
        clientId,
        checkInType: dashboardCheckInType
      });

      const docRef = await executeDashboardCheckInWrite(
        () => addDoc(collection(db, 'client_check_ins'), checkInData),
        `create ${dashboardCheckInType} check-in for ${clientId}`
      );

      console.info('Dashboard check-in: shortcut check-in created', {
        clientId,
        checkInType: dashboardCheckInType,
        checkInId: docRef.id
      });

      resetDashboardCheckInDialog();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Dashboard check-in: failed to submit shortcut check-in', {
        clientId,
        checkInType: dashboardCheckInType,
        error
      });
      setDashboardCheckInError('Unable to save this check-in. Please try again.');
    } finally {
      setIsSubmittingDashboardCheckIn(false);
    }
  };

  const handleClearAlerts = async () => {
    if (!selectedClientForClear || selectedAlertReasons.length === 0 || !clearAlertReason.trim()) {
      return;
    }

    setClearingAlert(true);
    
    try {
      const userId = user?.uid || 'unknown';
      const userName = user?.displayName || user?.email || 'Unknown User';
      
      // Create cleared alert record
      const clearedAlertData = {
        clientId: selectedClientForClear.client_id,
        clientName: selectedClientForClear.client_name,
        alertReasons: selectedAlertReasons,
        clearReason: clearAlertReason,
        clearedBy: userId,
        clearedByName: userName,
        clearedAt: serverTimestamp(),
      };
      
      // Store in Firestore
      const clearedAlertsRef = collection(db, 'ignition_alerts_cleared');
      await setDoc(doc(clearedAlertsRef), clearedAlertData);
      
      // Send notification to Google Chat webhook
      const webhookUrl = 'https://chat.googleapis.com/v1/spaces/AAQAtmpFPPU/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=ug_d7F2-7iYI6zUwOz56GDNreewNQlZB0qYNZ4kfbLg';
      
      const message = {
        text: `🚨 *Alert Cleared*\n\n*Client:* ${selectedClientForClear.client_name}\n*Alerts Cleared:*\n${selectedAlertReasons.map(reason => `• ${reason}`).join('\n')}\n\n*Cleared by:* ${userName}\n*Reason:* ${clearAlertReason}`
      };
      
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(message),
        });
      } catch (webhookError) {
        console.error('Failed to send Google Chat notification:', webhookError);
        // Continue even if webhook fails
      }
      
      // Close dialog and reset state
      setClearAlertDialogOpen(false);
      setClearAlertReason('');
      setSelectedAlertReasons([]);
      setSelectedClientForClear(null);
      
      // Trigger a refresh of dashboard data
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error clearing alerts:', error);
      alert('Failed to clear alerts. Please try again.');
    } finally {
      setClearingAlert(false);
    }
  };

  const handleClearFoundationAlerts = async () => {
    if (!selectedFoundationClientForClear || selectedFoundationAlertReasons.length === 0 || !clearFoundationAlertReason.trim()) {
      return;
    }

    setClearingFoundationAlert(true);
    
    try {
      const userId = user?.uid || 'unknown';
      const userName = user?.displayName || user?.email || 'Unknown User';
      
      const clearedAlertData = {
        clientId: selectedFoundationClientForClear.client_id,
        clientName: selectedFoundationClientForClear.client_name,
        alertReasons: selectedFoundationAlertReasons,
        clearReason: clearFoundationAlertReason,
        clearedBy: userId,
        clearedByName: userName,
        clearedAt: serverTimestamp(),
      };
      
      const clearedAlertsRef = collection(db, 'foundation_alerts_cleared');
      await setDoc(doc(clearedAlertsRef), clearedAlertData);
      
      const webhookUrl = 'https://chat.googleapis.com/v1/spaces/AAQAtmpFPPU/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=ug_d7F2-7iYI6zUwOz56GDNreewNQlZB0qYNZ4kfbLg';
      
      const message = {
        text: `🚨 *Foundation Alert Cleared*\n\n*Client:* ${selectedFoundationClientForClear.client_name}\n*Alerts Cleared:*\n${selectedFoundationAlertReasons.map(reason => `• ${reason}`).join('\n')}\n\n*Cleared by:* ${userName}\n*Reason:* ${clearFoundationAlertReason}`
      };
      
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(message),
        });
      } catch (webhookError) {
        console.error('Failed to send Google Chat notification for foundation alerts:', webhookError);
      }
      
      setFoundationClearAlertDialogOpen(false);
      setClearFoundationAlertReason('');
      setSelectedFoundationAlertReasons([]);
      setSelectedFoundationClientForClear(null);
      
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error clearing foundation alerts:', error);
      alert('Failed to clear foundation alerts. Please try again.');
    } finally {
      setClearingFoundationAlert(false);
    }
  };

  if (loading) {
    return <LoadingContainer loading={loading} minHeight="300px" message="LOADING DASHBOARD" />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
          {format(new Date(), 'EEEE, d MMMM yyyy')}
        </Typography>
      </Box>

      <CompactStatsBar
        sx={{ mb: 5 }}
        stats={[
          {
            icon: <PeopleIcon />,
            value: stats.totalClients,
            label: 'Total',
            color: 'primary',
            onClick: () => navigate('/clients'),
            tooltip: 'Total Clients'
          },
          {
            icon: <CheckCircleIcon />,
            value: stats.activeClients,
            label: 'Active',
            color: 'success',
            onClick: () => navigate('/clients'),
            tooltip: 'Active Clients'
          },
          {
            icon: <WarningIcon />,
            value: clientsWithAlerts.length,
            label: 'Alerts',
            color: 'error',
            onClick: () => scrollToSection(clientAlertsRef),
            tooltip: 'Client Alerts'
          },
          {
            icon: <FitnessCenterIcon />,
            value: clientsWithFoundationAlerts.length,
            label: 'Foundation',
            color: 'info',
            onClick: () => scrollToSection(foundationAlertsRef),
            tooltip: 'Foundation Client Alerts'
          },
          {
            icon: <ReportIcon />,
            value: stats.pendingReports,
            label: 'Reports',
            color: 'warning',
            onClick: () => scrollToSection(pendingReportsRef),
            tooltip: 'Pending Reports'
          },
          ...(userRole === 'ADMIN' || userRole === 'MANAGER' ? [{
            icon: <AssignmentIcon />,
            value: stats.pendingCheckins,
            label: 'Check-Ins',
            color: 'error' as const,
            onClick: () => scrollToSection(pendingCheckinsRef),
            tooltip: 'Outstanding Check-Ins'
          }] : []),
          {
            icon: <EmojiEventsIcon />,
            value: stats.outstandingSuccessUpdates,
            label: 'Updates',
            color: 'info',
            onClick: () => scrollToSection(successUpdatesRef),
            tooltip: 'Success Updates Required'
          },
          {
            icon: <AccessTimeIcon />,
            value: stats.upcomingSessions,
            label: 'Sessions',
            color: 'secondary',
            onClick: () => scrollToSection(upcomingSessionsRef),
            tooltip: 'Upcoming Sessions (Today & Tomorrow)'
          }
        ]}
      />

      {/* Client Alerts Section */}
      <Card ref={clientAlertsRef} elevation={1} sx={{ mb: 3, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'error.main' }}>
        <CardHeader 
          title="Ignition Client Alerts" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'error.main' }}>
              <WarningIcon />
            </Avatar>
          }
          action={
            clientsWithAlerts.length > 6 && (
              <Button 
                size="small" 
                onClick={() => setIsClientAlertsExpanded(!isClientAlertsExpanded)}
                endIcon={isClientAlertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isClientAlertsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {clientsWithAlerts.length > 0 ? (
            <Box>
              {(isClientAlertsExpanded ? clientsWithAlerts : clientsWithAlerts.slice(0, 6)).map((client, index) => (
                <Paper 
                  key={client.id}
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: index < clientsWithAlerts.length - 1 ? 1.5 : 0,
                    backgroundColor: theme.palette.error.dark,
                    borderRadius: 1,
                    color: theme.palette.error.contrastText,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={(e) => {
                    // Only navigate if not clicking the clear button
                    if (!(e.target as HTMLElement).closest('.clear-alert-button')) {
                      handleClientClick(client.id);
                    }
                  }}
                >
                  {(userRole === 'ADMIN' || userRole === 'MANAGER') && (
                    <IconButton
                      className="clear-alert-button"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: theme.palette.error.contrastText,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClientForClear(client);
                        setSelectedAlertReasons(client.alert_reasons || []);
                        setClearAlertDialogOpen(true);
                      }}
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  {/* Main Content Grid */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                            {client.client_name || 'Client #' + client.client_id}
                          </Typography>
                          {client.current_programme?.toLowerCase().includes('ignition') && (
                            <Chip
                              icon={<WhatshotIcon />}
                              label="Ignition"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: theme.palette.error.contrastText,
                                borderColor: 'rgba(255,255,255,0.4)',
                                border: '1px solid',
                                height: 20,
                                '& .MuiChip-icon': {
                                  fontSize: 14,
                                  color: theme.palette.error.contrastText
                                }
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnIcon sx={{ 
                              fontSize: 18, 
                              color: theme.palette.error.contrastText,
                              opacity: 0.7
                            }} />
                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                              {(() => {
                                const location = client.primary_location;
                                if (!location) return 'No location';
                                const locationLower = location.toLowerCase();
                                if (locationLower.includes('cbd')) return 'Club Street';
                                if (locationLower.includes('one north')) return 'one-north';
                                return location;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ 
                              fontSize: 18, 
                              color: theme.palette.error.contrastText,
                              opacity: 0.7
                            }} />
                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                              {client.lead_coach_name || 'No lead coach'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {/* Regular alerts only */}
                      {client.alert_reasons?.some(r => !r.toLowerCase().includes('report')) && (
                        <>
                          <Typography variant="body2" fontWeight="bold">
                            Alert Reasons:
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {client.alert_reasons
                              ?.filter(reason => !reason.toLowerCase().includes('report'))
                              .map((reason, i) => (
                                <Box 
                                  key={i} 
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 0.5
                                  }}
                                >
                                  <Box 
                                    component="span" 
                                    sx={{ 
                                      display: 'inline-block',
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      backgroundColor: 'currentColor',
                                      mr: 1,
                                      mt: 0.8
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {reason}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </>
                      )}
                      {/* Upcoming Appointments */}
                      {client.upcoming_appointments && client.upcoming_appointments.length > 0 && (() => {
                        const filteredAppointments = client.upcoming_appointments.filter(appointment => {
                          // Exclude appointments with program_id 14 & 2
                          const programId = appointment.program_id;
                          return programId !== 14 && programId !== '14' && programId !== 2 && programId !== '2';
                        });
                        
                        if (filteredAppointments.length === 0) return null;
                        
                        return (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Upcoming Appointments:
                            </Typography>
                            {filteredAppointments
                              .slice(0, 3) // Show only first 3 appointments
                              .map((appointment, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    • {formatSafeDate(appointment.date?.value || appointment.date, 'MMM dd, yyyy')} - {appointment.coach} @ {appointment.location}
                                  </Typography>
                                </Box>
                              ))}
                            {filteredAppointments.length > 3 && (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                ...and {filteredAppointments.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                      
                      {/* Progress Indicator for Ignition Clients */}
                      {client.current_programme?.toLowerCase().includes('ignition') && (() => {
                        // Extract total PT sessions from programme string (e.g., "Ignition Programme 2025 | 22 PT" -> 22)
                        const match = client.current_programme.match(/\|\s*(\d+)\s*PT/i);
                        const totalSessions = match ? parseInt(match[1]) : 0;
                        const completedSessions = client.pt_sessions || 0;
                        const progressPercentage = totalSessions > 0 ? Math.min((completedSessions / totalSessions) * 100, 100) : 0;
                        
                        // Calculate weeks since first ignition session
                        let weeksSinceStart: number | null = null;
                        if (client.first_ignition_session_date) {
                          const firstSessionDate = toDate(client.first_ignition_session_date);
                          if (firstSessionDate) {
                            weeksSinceStart = differenceInWeeks(new Date(), firstSessionDate);
                          }
                        }
                        
                        return totalSessions > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Programme Progress:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: theme.palette.error.contrastText }}>
                                PT Sessions: {completedSessions}/{totalSessions}
                              </Typography>
                              <Typography variant="caption" fontWeight="medium" sx={{ color: theme.palette.error.contrastText }}>
                                {Math.round(progressPercentage)}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={progressPercentage} 
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  background: progressPercentage === 100 
                                    ? 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)'
                                    : 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.9) 90%)',
                                }
                              }}
                            />
                            {weeksSinceStart !== null && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: theme.palette.error.contrastText, opacity: 0.9 }}>
                                  <AccessTimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  Week {weeksSinceStart + 1} of programme
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.palette.error.contrastText, opacity: 0.9 }}>
                                  {weeksSinceStart === 0 ? 'Started this week' : `${weeksSinceStart} week${weeksSinceStart !== 1 ? 's' : ''} in`}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : null;
                      })()}
                    </Grid>
                  </Grid>
                  
                  {/* Report Alerts - Bottom of card */}
                  {client.alert_reasons?.some(r => r.toLowerCase().includes('report')) && (
                    <Box 
                      sx={{ 
                        mt: 2,
                        mx: -2,
                        mb: -2,
                        p: 2,
                        background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
                        borderTop: `3px solid ${theme.palette.warning.light}`,
                        borderRadius: '0 0 4px 4px',
                      }}
                    >
                      {client.alert_reasons
                        ?.filter(reason => reason.toLowerCase().includes('report'))
                        .map((reason, i) => (
                          <Box 
                            key={i}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              mb: i < (client.alert_reasons?.filter(r => r.toLowerCase().includes('report')).length || 0) - 1 ? 1 : 0
                            }}
                          >
                            <Box sx={{ 
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              borderRadius: '50%',
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ReportIcon sx={{ 
                                color: theme.palette.warning.contrastText,
                                fontSize: 20
                              }} />
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{
                                flex: 1,
                                fontWeight: 'bold',
                                color: theme.palette.warning.contrastText,
                                fontSize: '0.9rem'
                              }}
                            >
                              {reason}
                            </Typography>
                            <Chip
                              label="URGENT"
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.error.main,
                                color: theme.palette.error.contrastText,
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          </Box>
                        ))}
                    </Box>
                  )}
                </Paper>
              ))}
              
              {!isClientAlertsExpanded && clientsWithAlerts.length > 6 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="error"
                    onClick={() => setIsClientAlertsExpanded(true)}
                    endIcon={<ExpandMoreIcon />}
                  >
                    Show {clientsWithAlerts.length - 6} More Alerts
                  </Button>
                </Box>
              )}

            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No client alerts at this time.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Foundation Client Alerts Section */}
      <Card ref={foundationAlertsRef} elevation={1} sx={{ mb: 3, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'info.main' }}>
        <CardHeader 
          title="Foundation Client Alerts" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'info.main' }}>
              <WarningIcon />
            </Avatar>
          }
          action={
            clientsWithFoundationAlerts.length > 6 && (
              <Button 
                size="small" 
                onClick={() => setIsFoundationAlertsExpanded(!isFoundationAlertsExpanded)}
                endIcon={isFoundationAlertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isFoundationAlertsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {clientsWithFoundationAlerts.length > 0 ? (
            <Box>
              {(isFoundationAlertsExpanded ? clientsWithFoundationAlerts : clientsWithFoundationAlerts.slice(0, 6)).map((client, index) => (
                <Paper 
                  key={`foundation-${client.id}`}
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: index < clientsWithFoundationAlerts.length - 1 ? 1.5 : 0,
                    backgroundColor: theme.palette.info.dark,
                    borderRadius: 1,
                    color: theme.palette.info.contrastText,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest('.clear-foundation-alert-button')) {
                      handleClientClick(client.id);
                    }
                  }}
                >
                  {(userRole === 'ADMIN' || userRole === 'MANAGER') && (
                    <IconButton
                      className="clear-foundation-alert-button"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: theme.palette.info.contrastText,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFoundationClientForClear(client);
                        setSelectedFoundationAlertReasons(client.foundation_alert_reasons || []);
                        setFoundationClearAlertDialogOpen(true);
                      }}
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                            {client.client_name || 'Client #' + client.client_id}
                          </Typography>
                          {client.current_programme?.toLowerCase().includes('foundation') && (
                            <Chip
                              icon={<WhatshotIcon />}
                              label="Foundation"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: theme.palette.info.contrastText,
                                borderColor: 'rgba(255,255,255,0.4)',
                                border: '1px solid',
                                height: 20,
                                '& .MuiChip-icon': {
                                  fontSize: 14,
                                  color: theme.palette.info.contrastText
                                }
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnIcon sx={{ 
                              fontSize: 18, 
                              color: theme.palette.info.contrastText,
                              opacity: 0.7
                            }} />
                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                              {(() => {
                                const location = client.primary_location;
                                if (!location) return 'No location';
                                const locationLower = location.toLowerCase();
                                if (locationLower.includes('cbd')) return 'Club Street';
                                if (locationLower.includes('one north')) return 'one-north';
                                return location;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ 
                              fontSize: 18, 
                              color: theme.palette.info.contrastText,
                              opacity: 0.7
                            }} />
                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                              {client.lead_coach_name || 'No lead coach'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {/* Regular alerts only */}
                      {client.foundation_alert_reasons?.some(r => !r.toLowerCase().includes('report')) && (
                        <>
                          <Typography variant="body2" fontWeight="bold">
                            Alert Reasons:
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {client.foundation_alert_reasons
                              ?.filter(reason => !reason.toLowerCase().includes('report'))
                              .map((reason, i) => (
                                <Box 
                                  key={i} 
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 0.5
                                  }}
                                >
                                  <Box 
                                    component="span" 
                                    sx={{ 
                                      display: 'inline-block',
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      backgroundColor: 'currentColor',
                                      mr: 1,
                                      mt: 0.8
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {reason}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </>
                      )}
                      {/* Upcoming Appointments */}
                      {client.upcoming_appointments && client.upcoming_appointments.length > 0 && (() => {
                        const filteredAppointments = client.upcoming_appointments.filter(appointment => {
                          const programId = appointment.program_id;
                          return programId !== 14 && programId !== '14' && programId !== 2 && programId !== '2';
                        });
                        
                        if (filteredAppointments.length === 0) return null;
                        
                        return (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Upcoming Appointments:
                            </Typography>
                            {filteredAppointments
                              .slice(0, 3)
                              .map((appointment, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    • {formatSafeDate(appointment.date?.value || appointment.date, 'MMM dd, yyyy')} - {appointment.coach} @ {appointment.location}
                                  </Typography>
                                </Box>
                              ))}
                            {filteredAppointments.length > 3 && (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                ...and {filteredAppointments.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                      
                      {/* Progress Indicator for Foundation Clients */}
                      {(() => {
                        const completedSessions = (client.foundation_pt_sessions || 0) + (client.foundation_pilates_sessions || 0);
                        const totalSessions = 14;
                        const progressPercentage = totalSessions > 0 ? Math.min((completedSessions / totalSessions) * 100, 100) : 0;

                        let weeksSinceStart: number | null = null;
                        if (client.first_foundation_session_date) {
                          const firstSessionDate = toDate(client.first_foundation_session_date);
                          if (firstSessionDate) {
                            weeksSinceStart = differenceInWeeks(new Date(), firstSessionDate);
                          }
                        }

                        return totalSessions > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Programme Progress:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: theme.palette.info.contrastText }}>
                                PT/Pilates Sessions: {completedSessions}/{totalSessions}
                              </Typography>
                              <Typography variant="caption" fontWeight="medium" sx={{ color: theme.palette.info.contrastText }}>
                                {Math.round(progressPercentage)}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={progressPercentage} 
                              sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  background: progressPercentage === 100 
                                    ? 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)'
                                    : 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.9) 90%)',
                                }
                              }}
                            />
                            {weeksSinceStart !== null && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: theme.palette.info.contrastText, opacity: 0.9 }}>
                                  <AccessTimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  Week {weeksSinceStart + 1} of programme
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.palette.info.contrastText, opacity: 0.9 }}>
                                  {weeksSinceStart === 0 ? 'Started this week' : `${weeksSinceStart} week${weeksSinceStart !== 1 ? 's' : ''} in`}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : null;
                      })()}
                    </Grid>
                  </Grid>
                  
                  {/* Report Alerts - Bottom of card */}
                  {client.foundation_alert_reasons?.some(r => r.toLowerCase().includes('report')) && (
                    <Box 
                      sx={{ 
                        mt: 2,
                        mx: -2,
                        mb: -2,
                        p: 2,
                        background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
                        borderTop: `3px solid ${theme.palette.warning.light}`,
                        borderRadius: '0 0 4px 4px',
                      }}
                    >
                      {client.foundation_alert_reasons
                        ?.filter(reason => reason.toLowerCase().includes('report'))
                        .map((reason, i) => (
                          <Box 
                            key={i}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              mb: i < (client.foundation_alert_reasons?.filter(r => r.toLowerCase().includes('report')).length || 0) - 1 ? 1 : 0
                            }}
                          >
                            <Box sx={{ 
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              borderRadius: '50%',
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ReportIcon sx={{ 
                                color: theme.palette.warning.contrastText,
                                fontSize: 20
                              }} />
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{
                                flex: 1,
                                fontWeight: 'bold',
                                color: theme.palette.warning.contrastText,
                                fontSize: '0.9rem'
                              }}
                            >
                              {reason}
                            </Typography>
                            <Chip
                              label="URGENT"
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.error.main,
                                color: theme.palette.error.contrastText,
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          </Box>
                        ))}
                    </Box>
                  )}
                </Paper>
              ))}
              
              {!isFoundationAlertsExpanded && clientsWithFoundationAlerts.length > 6 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    color="info"
                    onClick={() => setIsFoundationAlertsExpanded(true)}
                    endIcon={<ExpandMoreIcon />}
                  >
                    Show {clientsWithFoundationAlerts.length - 6} More Alerts
                  </Button>
                </Box>
              )}

            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No foundation client alerts at this time.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Ignition Expirations Section */}
      <Card elevation={1} sx={{ mb: 6, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
        <CardHeader 
          title="Upcoming Ignition Expirations" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'warning.main' }}>
              <AccessTimeIcon />
            </Avatar>
          }
          action={
            clientsWithUpcomingExpirations.length > 6 && (
              <Button 
                size="small" 
                onClick={() => setIsUpcomingExpirationsExpanded(!isUpcomingExpirationsExpanded)}
                endIcon={isUpcomingExpirationsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isUpcomingExpirationsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {clientsWithUpcomingExpirations.length > 0 ? (
            <Box>
              {/* Split clients by urgency levels */}
              {(() => {
                const criticalExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) <= 3);
                const highPriorityExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) > 3 && (c.days_until_expiration || 0) <= 7);
                const moderateExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) > 7 && (c.days_until_expiration || 0) <= 14);

                const renderExpirationCard = (client: any, index: number, displayedClients: any[], urgencyColor: 'error' | 'warning' | 'info') => {
                  const sessionProgress = client.total_sessions > 0
                    ? Math.min(((client.used_sessions || 0) / client.total_sessions) * 100, 100)
                    : 0;

                  // Determine session utilization health (reversed logic)
                  // High usage (near completion) = GREEN (good progress)
                  // Low usage = RED (concerning, not utilizing allocation)
                  const daysRemaining = client.days_until_expiration || 0;
                  const sessionsRemaining = client.remaining_sessions || 0;
                  const sessionHealthColor = sessionProgress >= 90
                    ? 'success'      // 90%+ used = Green (excellent progress)
                    : sessionProgress >= 50
                      ? 'warning'    // 50-89% used = Yellow (on track)
                      : 'error';     // < 50% used = Red (low utilization)

                  return (
                    <Paper
                      key={client.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: index < displayedClients.length - 1 ? 1 : 0,
                        backgroundColor: theme.palette[urgencyColor].light,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: `1px solid ${theme.palette[urgencyColor].main}`,
                        '&:hover': {
                          backgroundColor: theme.palette[urgencyColor].main,
                          '& .MuiTypography-root': {
                            color: theme.palette[urgencyColor].contrastText
                          },
                          '& .MuiSvgIcon-root': {
                            color: theme.palette[urgencyColor].contrastText
                          }
                        }
                      }}
                      onClick={() => handleClientClick(client.id)}
                    >
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 0.5, sm: 2 },
                        overflow: 'hidden'
                      }}>
                        {/* Client Name + Ignition Badge */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: { xs: 'auto', sm: 1 }
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                              {client.client_name || 'Client #' + client.client_id}
                            </Typography>
                          </Box>
                          <Chip
                            icon={<WhatshotIcon />}
                            label="Ignition"
                            size="small"
                            color="primary"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              '& .MuiChip-icon': {
                                fontSize: 14
                              }
                            }}
                          />
                        </Box>

                        {/* Details Row */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: { xs: 'auto', sm: 'none' }
                        }}>
                          {/* Coach */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                            <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                            <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {client.lead_coach_name || 'No coach'}
                            </Typography>
                          </Box>

                          {/* Location */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                            <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                            <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {(() => {
                                const location = client.primary_location;
                                if (!location) return 'No location';
                                const locationLower = location.toLowerCase();
                                if (locationLower.includes('cbd')) return 'Club Street';
                                if (locationLower.includes('one north')) return 'one-north';
                                return location;
                              })()}
                            </Typography>
                          </Box>

                          {/* Sessions Progress */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto', minWidth: 120 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                              {client.used_sessions || 0}/{client.total_sessions || 0}
                            </Typography>
                            <Box sx={{ flexGrow: 1, minWidth: 60 }}>
                              <LinearProgress
                                variant="determinate"
                                value={sessionProgress}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: alpha(theme.palette[sessionHealthColor].main, 0.1),
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: theme.palette[sessionHealthColor].main,
                                    borderRadius: 3,
                                  }
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Expiration Date */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto' }}>
                            <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}
                            >
                              {client.ignition_expiration_date ? formatSafeDate(client.ignition_expiration_date, 'MMM dd') : 'N/A'}
                            </Typography>
                          </Box>

                          {/* Days Until Expiration Badge */}
                          <Chip
                            label={`${daysRemaining}d`}
                            size="small"
                            color={urgencyColor}
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              minWidth: 45
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                };

                return (
                  <>
                    {/* Critical - Immediate Action (0-3 days) */}
                    {criticalExpirations.length > 0 && (
                      <Box sx={{ mb: (highPriorityExpirations.length > 0 || moderateExpirations.length > 0) ? 4 : 0 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: theme.palette.error.light,
                          border: `2px solid ${theme.palette.error.main}`
                        }}>
                          <WarningIcon sx={{ color: theme.palette.error.dark, fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.error.dark }}>
                             Expiring Imminently
                          </Typography>
                          <Chip
                            label={criticalExpirations.length}
                            color="error"
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 35 }}
                          />
                        </Box>

                        {criticalExpirations
                          .slice(0, isUpcomingExpirationsExpanded ? criticalExpirations.length : criticalExpirations.length)
                          .map((client, index, displayedClients) => renderExpirationCard(client, index, displayedClients, 'error'))}
                      </Box>
                    )}

                    {/* High Priority - This Week (4-7 days) */}
                    {highPriorityExpirations.length > 0 && (
                      <Box sx={{ mb: moderateExpirations.length > 0 ? 4 : 0 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: theme.palette.warning.light,
                        }}>
                          <AccessTimeIcon sx={{ color: theme.palette.warning.dark, fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.warning.dark }}>
                            Expiring This Week
                          </Typography>
                          <Chip
                            label={highPriorityExpirations.length}
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 35 }}
                          />
                        </Box>

                        {highPriorityExpirations
                          .slice(0, isUpcomingExpirationsExpanded ? highPriorityExpirations.length : 3)
                          .map((client, index, displayedClients) => renderExpirationCard(client, index, displayedClients, 'warning'))}
                      </Box>
                    )}

                    {/* Moderate - Planning Needed (8-14 days) */}
                    {moderateExpirations.length > 0 && (
                      <Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: theme.palette.info.light,
                        }}>
                          <CheckCircleIcon sx={{ color: theme.palette.info.dark, fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.info.dark }}>
                            Expiring Next Week
                          </Typography>
                          <Chip
                            label={moderateExpirations.length}
                            color="info"
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 35 }}
                          />
                        </Box>

                        {moderateExpirations
                          .slice(0, isUpcomingExpirationsExpanded ? moderateExpirations.length : 3)
                          .map((client, index, displayedClients) => renderExpirationCard(client, index, displayedClients, 'info'))}
                      </Box>
                    )}
                  </>
                );
              })()}

              {/* Old cards - hidden */}
              {false && (isUpcomingExpirationsExpanded ? clientsWithUpcomingExpirations : clientsWithUpcomingExpirations.slice(0, 6)).map((client, index) => (
                <Paper 
                  key={client.id}
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: index < clientsWithUpcomingExpirations.length - 1 ? 1.5 : 0,
                    backgroundColor: theme.palette.error.light,
                    borderRadius: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleClientClick(client.id)}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                            {client.client_name || 'Client #' + client.client_id}
                          </Typography>
                          {client.current_programme?.toLowerCase().includes('ignition') && (
                            <Chip
                              icon={<WhatshotIcon />}
                              label="Ignition"
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.warning.main,
                                color: theme.palette.warning.contrastText,
                                height: 20,
                                fontWeight: 'bold',
                                '& .MuiChip-icon': {
                                  fontSize: 14,
                                  color: theme.palette.warning.contrastText
                                }
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {(() => {
                                const location = client.primary_location;
                                if (!location) return 'No location';
                                const locationLower = location.toLowerCase();
                                if (locationLower.includes('cbd')) return 'Club Street';
                                if (locationLower.includes('one north')) return 'one-north';
                                return location;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {client.lead_coach_name || 'No lead coach'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body2" fontWeight="bold">
                        Expiration Details:
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Days Until Expiration:</strong> {client.days_until_expiration}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Expiration Date:</strong> {client.ignition_expiration_date ? 
                            formatSafeDate(client.ignition_expiration_date, 'MMM dd, yyyy') : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Current Programme:</strong> {client.current_programme || 'N/A'}
                        </Typography>
                        {/* PT Sessions Progress Bar */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            PT Sessions Progress:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">
                              {client.used_sessions || 0} used / {client.total_sessions || 0} total
                            </Typography>
                            <Typography variant="caption" fontWeight="medium">
                              {client.total_sessions > 0 ? Math.round(((client.used_sessions || 0) / client.total_sessions) * 100) : 0}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={client.total_sessions > 0 ? Math.min(((client.used_sessions || 0) / client.total_sessions) * 100, 100) : 0} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: client.remaining_sessions === 0 
                                  ? `linear-gradient(45deg, ${theme.palette.error.main} 30%, ${theme.palette.error.light} 90%)`
                                  : `linear-gradient(45deg, ${theme.palette.warning.main} 30%, ${theme.palette.warning.light} 90%)`,
                              }
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              mt: 0.5,
                              color: client.remaining_sessions === 0 ? theme.palette.error.main : 'text.secondary'
                            }}
                          >
                            {client.remaining_sessions || 0} sessions remaining
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          <strong>Accessory Sessions Used:</strong> Nutrition [{client.nutrition_sessions}] | Metabolic [{client.metabolic_screenings}] | Physical Performance [{client.physical_performance_screenings}] 
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              
              {(() => {
                const criticalExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) <= 3);
                const highPriorityExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) > 3 && (c.days_until_expiration || 0) <= 7);
                const moderateExpirations = clientsWithUpcomingExpirations.filter(c => (c.days_until_expiration || 0) > 7 && (c.days_until_expiration || 0) <= 14);

                const totalShown = isUpcomingExpirationsExpanded
                  ? clientsWithUpcomingExpirations.length
                  : criticalExpirations.length + Math.min(highPriorityExpirations.length, 3) + Math.min(moderateExpirations.length, 3);
                const totalHidden = clientsWithUpcomingExpirations.length - totalShown;

                return !isUpcomingExpirationsExpanded && totalHidden > 0 && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      onClick={() => setIsUpcomingExpirationsExpanded(true)}
                      endIcon={<ExpandMoreIcon />}
                    >
                      Show {totalHidden} More
                    </Button>
                  </Box>
                );
              })()}
              
            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No upcoming ignition expirations at this time.
            </Typography>
          )}
        </CardContent>
      </Card>

{/* Zone 2 — Pending Actions */}
<Box sx={{ mb: 3, mt: 1 }}>
  <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.disabled', letterSpacing: '0.1em' }}>
    Pending Actions
  </Typography>
  <Divider sx={{ mt: 0.5 }} />
</Box>

{/* Pending Reports */}
<Card ref={pendingReportsRef} elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
       <CardHeader 
         title="Pending Reports" 
         titleTypographyProps={{ variant: 'h6' }}
         avatar={
           <Avatar sx={{ bgcolor: 'warning.main' }}>
             <ReportIcon />
           </Avatar>
         }
         action={
           clientsWithPendingReports.length > 6 && (
             <Button 
               size="small" 
               onClick={() => setIsPendingReportsExpanded(!isPendingReportsExpanded)}
               endIcon={isPendingReportsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
             >
               {isPendingReportsExpanded ? 'Show Less' : 'Show More'}
             </Button>
           )
         }
       />
       <Divider />
       <CardContent>
         {clientsWithPendingReports.length > 0 ? (
           <Box>
             {(() => {
	               // Split reports into three categories (Ignition + Foundation)
	               const isPendingInitial = (client: any) =>
	                 client.pending_report_initial === true || client.foundation_pending_report_initial === true;
	               const isPendingReview = (client: any) =>
	                 client.pending_report_review === true || client.foundation_pending_report_review === true;

	               const bothReports = clientsWithPendingReports.filter(c => isPendingInitial(c) && isPendingReview(c));
	               const initialReports = clientsWithPendingReports.filter(c => isPendingInitial(c) && !isPendingReview(c));
	               const reviewReports = clientsWithPendingReports.filter(c => !isPendingInitial(c) && isPendingReview(c));

               // Render function for compact report cards
	               const renderReportCard = (client: any, index: number, displayedClients: any[], colorTheme: 'error' | 'warning' | 'info') => {
	                 const isFoundationClient =
	                   client.foundation_pending_report_initial === true || client.foundation_pending_report_review === true;
	                 // Calculate last session recency for badge color
	                 const lastSessionDate = client.visit_dates && client.visit_dates.length > 0
	                   ? toDate(client.visit_dates[client.visit_dates.length - 1])
	                   : null;
                 const daysSinceLastSession = lastSessionDate
                   ? Math.floor((new Date().getTime() - lastSessionDate.getTime()) / (1000 * 60 * 60 * 24))
                   : 999;

                 const sessionBadgeColor = daysSinceLastSession < 7
                   ? 'success'
                   : daysSinceLastSession < 14
                     ? 'warning'
                     : 'error';

                 return (
                   <Paper
                     key={client.id}
                     elevation={0}
                     sx={{
                       p: 1.5,
                       mb: index < displayedClients.length - 1 ? 1 : 0,
                       backgroundColor: alpha(theme.palette[colorTheme].main, 0.1),
                       borderLeft: `4px solid ${theme.palette[colorTheme].main}`,
                       borderRadius: 1,
                       cursor: 'pointer',
                       transition: 'all 0.2s ease-in-out',
                       '&:hover': {
                         backgroundColor: alpha(theme.palette[colorTheme].main, 0.2),
                         transform: 'translateX(4px)',
                       }
                     }}
                     onClick={() => handleClientClick(client.id)}
                   >
                     <Box sx={{
                       display: 'flex',
                       flexDirection: { xs: 'column', sm: 'row' },
                       alignItems: { xs: 'stretch', sm: 'center' },
                       gap: { xs: 1, sm: 2 },
                       overflow: 'hidden'
                     }}>
	                       {/* Client Name + Programme */}
	                       <Box sx={{ flex: '1 1 200px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
	                         <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0, flex: 1 }}>
	                           {client.client_name || 'Client #' + client.client_id}
	                         </Typography>
	                         {isFoundationClient ? (
	                           <Chip
	                             icon={<FitnessCenterIcon />}
	                             label="Foundation"
	                             size="small"
	                             color="secondary"
	                             sx={{
	                               height: 20,
	                               fontSize: '0.7rem',
	                               '& .MuiChip-icon': {
	                                 fontSize: 14,
	                               },
	                             }}
	                           />
	                         ) : (
	                           <Chip
	                             icon={<WhatshotIcon />}
	                             label="Ignition"
	                             size="small"
	                             color="primary"
	                             sx={{
	                               height: 20,
	                               fontSize: '0.7rem',
	                               '& .MuiChip-icon': {
	                                 fontSize: 14,
	                               },
	                             }}
	                           />
	                         )}
	                       </Box>

                       {/* Coach */}
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 1 150px', minWidth: 0 }}>
                         <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                         <Typography variant="body2" noWrap sx={{ fontSize: '0.813rem' }}>
                           {client.lead_coach_name || 'Unassigned'}
                         </Typography>
                       </Box>

                       {/* Location */}
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 1 120px', minWidth: 0 }}>
                         <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                         <Typography variant="body2" noWrap sx={{ fontSize: '0.813rem' }}>
                           {(() => {
                             const location = client.primary_location;
                             if (!location) return 'No location';
                             const locationLower = location.toLowerCase();
                             if (locationLower.includes('cbd')) return 'Club St';
                             if (locationLower.includes('one north')) return 'one-north';
                             return location;
                           })()}
                         </Typography>
                       </Box>

                       {/* Last Session Badge */}
                       <Box sx={{ flex: '0 0 auto' }}>
                         <Chip
                           label={lastSessionDate ? formatSafeDate(lastSessionDate, 'MMM dd') : 'No session'}
                           size="small"
                           color={sessionBadgeColor}
                           sx={{
                             height: 24,
                             fontSize: '0.75rem',
                             fontWeight: 600,
                           }}
                         />
                       </Box>

                       {/* Expiration Badge (if available) */}
                       {client.ignition_expiration_date && (
                         <Box sx={{ flex: '0 0 auto' }}>
                           <Chip
                             label={`Exp: ${formatSafeDate(client.ignition_expiration_date, 'MMM dd')}`}
                             size="small"
                             variant="outlined"
                             sx={{
                               height: 24,
                               fontSize: '0.75rem',
                               borderColor: theme.palette[colorTheme].main,
                               color: theme.palette[colorTheme].dark,
                             }}
                           />
                         </Box>
                       )}
                     </Box>
                   </Paper>
                 );
               };

               return (
                 <>
                   {/* Both Initial & Review Reports - Highest Priority */}
                   {bothReports.length > 0 && (
                     <Box sx={{ mb: (initialReports.length > 0 || reviewReports.length > 0) ? 4 : 0 }}>
                       <Box sx={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 1.5,
                         mb: 2,
                         p: 1.5,
                         borderRadius: 2,
                         backgroundColor: theme.palette.error.light,
                         border: `2px solid ${theme.palette.error.main}`
                       }}>
                         <WarningIcon sx={{ color: theme.palette.error.dark, fontSize: 24 }} />
                         <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.error.dark }}>
                           Both Initial & Review Reports Required
                         </Typography>
                         <Chip
                           label={bothReports.length}
                           color="error"
                           size="small"
                           sx={{ fontWeight: 700, minWidth: 35 }}
                         />
                       </Box>

                       {bothReports
                         .slice(0, isPendingReportsExpanded ? bothReports.length : 3)
                         .map((client, index, displayedClients) => renderReportCard(client, index, displayedClients, 'error'))}
                     </Box>
                   )}

                   {/* Initial Reports */}
                   {initialReports.length > 0 && (
                     <Box sx={{ mb: reviewReports.length > 0 ? 4 : 0 }}>
                       <Box sx={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 1.5,
                         mb: 2,
                         p: 1.5,
                         borderRadius: 2,
                         backgroundColor: theme.palette.warning.light,
                       }}>
                         <AssignmentIcon sx={{ color: theme.palette.warning.dark, fontSize: 24 }} />
                         <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.warning.dark }}>
                           Initial Reports Required
                         </Typography>
                         <Chip
                           label={initialReports.length}
                           color="warning"
                           size="small"
                           sx={{ fontWeight: 700, minWidth: 35 }}
                         />
                       </Box>

                       {initialReports
                         .slice(0, isPendingReportsExpanded ? initialReports.length : 3)
                         .map((client, index, displayedClients) => renderReportCard(client, index, displayedClients, 'warning'))}
                     </Box>
                   )}

                   {/* Review Reports */}
                   {reviewReports.length > 0 && (
                     <Box>
                       <Box sx={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 1.5,
                         mb: 2,
                         p: 1.5,
                         borderRadius: 2,
                         backgroundColor: theme.palette.info.light,
                       }}>
                         <RateReviewIcon sx={{ color: theme.palette.info.dark, fontSize: 24 }} />
                         <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.info.dark }}>
                           Review Reports Required
                         </Typography>
                         <Chip
                           label={reviewReports.length}
                           color="info"
                           size="small"
                           sx={{ fontWeight: 700, minWidth: 35 }}
                         />
                       </Box>

                       {reviewReports
                         .slice(0, isPendingReportsExpanded ? reviewReports.length : 3)
                         .map((client, index, displayedClients) => renderReportCard(client, index, displayedClients, 'info'))}
                     </Box>
                   )}
                 </>
               );
             })()}

	             {(() => {
	               const isPendingInitial = (client: any) =>
	                 client.pending_report_initial === true || client.foundation_pending_report_initial === true;
	               const isPendingReview = (client: any) =>
	                 client.pending_report_review === true || client.foundation_pending_report_review === true;

	               const bothReports = clientsWithPendingReports.filter(c => isPendingInitial(c) && isPendingReview(c));
	               const initialReports = clientsWithPendingReports.filter(c => isPendingInitial(c) && !isPendingReview(c));
	               const reviewReports = clientsWithPendingReports.filter(c => !isPendingInitial(c) && isPendingReview(c));

               const totalShown = isPendingReportsExpanded
                 ? clientsWithPendingReports.length
                 : Math.min(bothReports.length, 3) + Math.min(initialReports.length, 3) + Math.min(reviewReports.length, 3);
               const totalHidden = clientsWithPendingReports.length - totalShown;

               return !isPendingReportsExpanded && totalHidden > 0 && (
                 <Box sx={{ textAlign: 'center', mt: 2 }}>
                   <Button
                     variant="outlined"
                     size="small"
                     color="warning"
                     onClick={() => setIsPendingReportsExpanded(true)}
                     endIcon={<ExpandMoreIcon />}
                   >
                     Show {totalHidden} More
                   </Button>
                 </Box>
               );
             })()}
           </Box>
         ) : (
           <Typography variant="body1" align="center" py={4}>
             No clients currently have pending reports.
           </Typography>
         )}
       </CardContent>
     </Card>
 {/* Zone 3 — Activity & Insights */}
 <Box sx={{ mb: 3, mt: 1 }}>
   <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.disabled', letterSpacing: '0.1em' }}>
     Activity & Insights
   </Typography>
   <Divider sx={{ mt: 0.5 }} />
 </Box>

 {/* Upcoming Sessions */}
 <Card ref={upcomingSessionsRef} elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader 
          title="Upcoming Sessions" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <AccessTimeIcon />
            </Avatar>
          }
          action={
            clientsWithUpcomingSessions.length > 6 && (
              <Button 
                size="small" 
                onClick={() => setIsUpcomingSessionsExpanded(!isUpcomingSessionsExpanded)}
                endIcon={isUpcomingSessionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isUpcomingSessionsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {clientsWithUpcomingSessions.length > 0 ? (
            <Box>
              {/* Today's Sessions */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'success.main' }}>
                Today
              </Typography>
              {(() => {
                // Process appointments for today and sort by time
                
                // Filter clients with appointments today using the timezone-aware function
                const todayClients = clientsWithUpcomingSessions.filter(client => 
                  client.upcoming_appointments?.some(appointment => {
                    const dateValue = appointment.date?.value || appointment.date;
                    return dateValue && isToday(dateValue);
                  })
                );
                
                // Sort clients by their earliest appointment time today
                const sortedTodayClients = [...todayClients].sort((a, b) => {
                  // Get earliest appointment time for client A
                  const earliestAppointmentA = a.upcoming_appointments
                    ?.filter(appt => {
                      const dateValue = appt.date?.value || appt.date;
                      return dateValue && isToday(dateValue);
                    })
                    .sort((x, y) => {
                      const timeX = getMinutesSinceMidnight(x.date?.value || x.date);
                      const timeY = getMinutesSinceMidnight(y.date?.value || y.date);
                      return timeX - timeY;
                    })[0];
                  
                  // Get earliest appointment time for client B
                  const earliestAppointmentB = b.upcoming_appointments
                    ?.filter(appt => {
                      const dateValue = appt.date?.value || appt.date;
                      return dateValue && isToday(dateValue);
                    })
                    .sort((x, y) => {
                      const timeX = getMinutesSinceMidnight(x.date?.value || x.date);
                      const timeY = getMinutesSinceMidnight(y.date?.value || y.date);
                      return timeX - timeY;
                    })[0];
                  
                  // Compare the earliest appointment times
                  if (!earliestAppointmentA) return 1;
                  if (!earliestAppointmentB) return -1;
                  
                  const timeA = getMinutesSinceMidnight(earliestAppointmentA.date?.value || earliestAppointmentA.date);
                  const timeB = getMinutesSinceMidnight(earliestAppointmentB.date?.value || earliestAppointmentB.date);
                  return timeA - timeB;
                });
                
                const displayedTodayClients = isUpcomingSessionsExpanded 
                  ? sortedTodayClients 
                  : sortedTodayClients.slice(0, Math.min(3, sortedTodayClients.length));
                  
                return (
                  <>
                    {displayedTodayClients.map((client, index, array) => {
                      // Get today's appointments for this client using our timezone-aware function
                      const todayAppointments = client.upcoming_appointments?.filter(appointment => {
                        const dateValue = appointment.date?.value || appointment.date;
                        if (!dateValue) {
                          console.warn('Appointment with no date value:', appointment);
                          return false;
                        }
                        return isToday(dateValue);
                      }) || [];
                      
                      return (
                        <Paper 
                          key={`today-${client.id}`}
                          elevation={0} 
                          sx={{ 
                            p: 1.5, 
                            mb: index < array.length - 1 ? 1 : 0,
                            backgroundColor: theme.palette.success.light,
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: theme.palette.success.main,
                              '& .MuiTypography-root': {
                                color: theme.palette.success.contrastText
                              },
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.success.contrastText
                              }
                            }
                          }}
                          onClick={() => handleClientClick(client.id)}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' }, 
                            gap: { xs: 0.5, sm: 2 },
                            overflow: 'hidden'
                          }}>
                            {/* Top row on mobile: Time and Client Name */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              flex: { xs: 'auto', sm: 1 }
                            }}>
                              {/* Time */}
                              <Box sx={{ minWidth: 60 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {todayAppointments.length > 0 && todayAppointments[0]
                                    ? formatTime(todayAppointments[0].date?.value || todayAppointments[0].date)
                                    : '--:--'
                                  }
                                </Typography>
                              </Box>
                              
                              {/* Client Name */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {client.client_name || 'Client #' + client.client_id}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Bottom row on mobile: Coach and Location */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              pl: { xs: 9, sm: 0 } // Indent to align with client name on mobile
                            }}>
                              {/* Coach with icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {todayAppointments.length > 0 
                                    ? todayAppointments[0].coach
                                    : client.lead_coach_name || 'Unassigned'
                                  }
                                </Typography>
                              </Box>
                              
                              {/* Location with icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {(() => {
                                    const appointmentLocation = todayAppointments.length > 0 
                                      ? todayAppointments[0].location
                                      : client.primary_location;
                                    
                                    if (!appointmentLocation) return 'N/A';
                                    const locationLower = appointmentLocation.toLowerCase();
                                    if (locationLower.includes('cbd')) return 'Club Street';
                                    if (locationLower.includes('one north')) return 'one-north';
                                    return appointmentLocation;
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                    
                    {!isUpcomingSessionsExpanded && todayClients.length > 3 && (
                      <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          color="success"
                          onClick={() => setIsUpcomingSessionsExpanded(true)}
                          endIcon={<ExpandMoreIcon />}
                        >
                          Show {todayClients.length - 3} More Today
                        </Button>
                      </Box>
                    )}
                    
                    {todayClients.length === 0 && (
                      <Typography variant="body2" sx={{ mb: 3, ml: 2 }}>
                        No sessions scheduled for today.
                      </Typography>
                    )}
                  </>
                );
              })()}
              
              {/* Tomorrow's Sessions */}
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'warning.main' }}>
                Tomorrow
              </Typography>
              {(() => {
                // Filter clients with appointments tomorrow using the timezone-aware function
                const tomorrowClients = clientsWithUpcomingSessions.filter(client => 
                  client.upcoming_appointments?.some(appointment => {
                    const dateValue = appointment.date?.value || appointment.date;
                    return dateValue && isTomorrow(dateValue);
                  })
                );
                
                // Sort clients by their earliest appointment time tomorrow
                const sortedTomorrowClients = [...tomorrowClients].sort((a, b) => {
                  // Get earliest appointment time for client A
                  const earliestAppointmentA = a.upcoming_appointments
                    ?.filter(appt => {
                      const dateValue = appt.date?.value || appt.date;
                      return dateValue && isTomorrow(dateValue);
                    })
                    .sort((x, y) => {
                      const timeX = getMinutesSinceMidnight(x.date?.value || x.date);
                      const timeY = getMinutesSinceMidnight(y.date?.value || y.date);
                      return timeX - timeY;
                    })[0];
                  
                  // Get earliest appointment time for client B
                  const earliestAppointmentB = b.upcoming_appointments
                    ?.filter(appt => {
                      const dateValue = appt.date?.value || appt.date;
                      return dateValue && isTomorrow(dateValue);
                    })
                    .sort((x, y) => {
                      const timeX = getMinutesSinceMidnight(x.date?.value || x.date);
                      const timeY = getMinutesSinceMidnight(y.date?.value || y.date);
                      return timeX - timeY;
                    })[0];
                  
                  // Compare the earliest appointment times
                  if (!earliestAppointmentA) return 1;
                  if (!earliestAppointmentB) return -1;
                  
                  const timeA = getMinutesSinceMidnight(earliestAppointmentA.date?.value || earliestAppointmentA.date);
                  const timeB = getMinutesSinceMidnight(earliestAppointmentB.date?.value || earliestAppointmentB.date);
                  return timeA - timeB;
                });
                
                const displayedTomorrowClients = isUpcomingSessionsExpanded 
                  ? sortedTomorrowClients 
                  : sortedTomorrowClients.slice(0, Math.min(3, sortedTomorrowClients.length));
                  
                return (
                  <>
                    {displayedTomorrowClients.map((client, index, array) => {
                      // Get tomorrow's appointments for this client using our timezone-aware function
                      const tomorrowAppointments = client.upcoming_appointments?.filter(appointment => {
                        const dateValue = appointment.date?.value || appointment.date;
                        if (!dateValue) {
                          console.warn('Tomorrow appointment with no date value:', appointment);
                          return false;
                        }
                        return isTomorrow(dateValue);
                      }) || [];
                      
                      return (
                        <Paper 
                          key={`tomorrow-${client.id}`}
                          elevation={0} 
                          sx={{ 
                            p: 1.5, 
                            mb: index < array.length - 1 ? 1 : 0,
                            backgroundColor: theme.palette.warning.light,
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: theme.palette.warning.main,
                              '& .MuiTypography-root': {
                                color: theme.palette.warning.contrastText
                              },
                              '& .MuiSvgIcon-root': {
                                color: theme.palette.warning.contrastText
                              }
                            }
                          }}
                          onClick={() => handleClientClick(client.id)}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' }, 
                            gap: { xs: 0.5, sm: 2 },
                            overflow: 'hidden'
                          }}>
                            {/* Top row on mobile: Time and Client Name */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              flex: { xs: 'auto', sm: 1 }
                            }}>
                              {/* Time */}
                              <Box sx={{ minWidth: 60 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {tomorrowAppointments.length > 0 && tomorrowAppointments[0]
                                    ? formatTime(tomorrowAppointments[0].date?.value || tomorrowAppointments[0].date)
                                    : '--:--'
                                  }
                                </Typography>
                              </Box>
                              
                              {/* Client Name */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {client.client_name || 'Client #' + client.client_id}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Bottom row on mobile: Coach and Location */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              pl: { xs: 9, sm: 0 } // Indent to align with client name on mobile
                            }}>
                              {/* Coach with icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: { xs: 0, sm: 120 } }}>
                                <PersonIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {tomorrowAppointments.length > 0 
                                    ? tomorrowAppointments[0].coach
                                    : client.lead_coach_name || 'Unassigned'
                                  }
                                </Typography>
                              </Box>
                              
                              {/* Location with icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: { xs: 0, sm: 100 } }}>
                                <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {(() => {
                                    const appointmentLocation = tomorrowAppointments.length > 0 
                                      ? tomorrowAppointments[0].location
                                      : client.primary_location;
                                    
                                    if (!appointmentLocation) return 'N/A';
                                    const locationLower = appointmentLocation.toLowerCase();
                                    if (locationLower.includes('cbd')) return 'Club Street';
                                    if (locationLower.includes('one north')) return 'one-north';
                                    return appointmentLocation;
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                    
                    {!isUpcomingSessionsExpanded && tomorrowClients.length > 3 && (
                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          color="warning"
                          onClick={() => setIsUpcomingSessionsExpanded(true)}
                          endIcon={<ExpandMoreIcon />}
                        >
                          Show {tomorrowClients.length - 3} More Tomorrow
                        </Button>
                      </Box>
                    )}
                    
                    {tomorrowClients.length === 0 && (
                      <Typography variant="body2" sx={{ mb: 3, ml: 2 }}>
                        No sessions scheduled for tomorrow.
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No upcoming sessions for today or tomorrow.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Recent Clients */}
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader 
          title="Recent Sessions" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PeopleIcon />
            </Avatar>
          }
          action={
            recentClients.length > 6 && (
              <Button 
                size="small" 
                onClick={() => setIsRecentSessionsExpanded(!isRecentSessionsExpanded)}
                endIcon={isRecentSessionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isRecentSessionsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {recentClients.length > 0 ? (
            <Box>
              {recentClients
                .slice(0, isRecentSessionsExpanded ? recentClients.length : 6)
                .map((client, index, displayedClients) => (
                <Paper 
                  key={client.id}
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    mb: index < displayedClients.length - 1 ? 1 : 0,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                  onClick={() => handleClientClick(client.id)}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' }, 
                    gap: { xs: 0.5, sm: 2 },
                    overflow: 'hidden'
                  }}>
                    {/* Top row on mobile: Client Name and Session Count */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      flex: { xs: 'auto', sm: 1 }
                    }}>
                      {/* Client Name */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {client.client_name || 'Client #' + client.client_id}
                        </Typography>
                      </Box>
                      
                      {/* Session Count - visible on mobile */}
                      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        <Chip 
                          label={client.visit_dates ? client.visit_dates.length : 0}
                          size="small"
                          sx={{ 
                            height: 20,
                            fontSize: '0.75rem',
                            backgroundColor: client.active_programme ? 'success.light' : 'action.selected'
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Bottom row on mobile: Coach, Location, Last Session */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      flex: { xs: 'auto', sm: 'none' }
                    }}>
                      {/* Coach with icon */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                        <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {client.lead_coach_name || 'No lead coach'}
                        </Typography>
                      </Box>
                      
                      {/* Location with icon */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                        <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const location = client.primary_location;
                            if (!location) return 'No location';
                            const locationLower = location.toLowerCase();
                            if (locationLower.includes('cbd')) return 'Club Street';
                            if (locationLower.includes('one north')) return 'one-north';
                            return location;
                          })()}
                        </Typography>
                      </Box>
                      
                      {/* Last Session Date */}
                      <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {(() => {
                            if (!client.visit_dates || client.visit_dates.length === 0) return 'No sessions';
                            
                            const lastVisitDate = client.visit_dates[client.visit_dates.length - 1];
                            try {
                              const date = toDate(lastVisitDate);
                              if (!date) {
                                console.warn('Invalid visit date:', lastVisitDate);
                                return 'Invalid date';
                              }
                              return format(date, 'MMM dd');
                            } catch (error) {
                              console.error('Error formatting visit date:', error, lastVisitDate);
                              return 'Invalid date';
                            }
                          })()}
                        </Typography>
                      </Box>
                      
                      {/* Session Count - hidden on mobile (shown above) */}
                      <Box sx={{ flex: '0 0 auto', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                        <Chip 
                          label={client.visit_dates ? client.visit_dates.length : 0}
                          size="small"
                          sx={{ 
                            height: 20,
                            fontSize: '0.75rem',
                            backgroundColor: client.active_programme ? 'success.light' : 'action.selected'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
              {!isRecentSessionsExpanded && recentClients.length > 6 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsRecentSessionsExpanded(true)}
                    endIcon={<ExpandMoreIcon />}
                  >
                    Show {recentClients.length - 6} More
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No recent clients.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Only show Outstanding Check-ins for ADMIN and MANAGER roles */}
      {(userRole === 'ADMIN' || userRole === 'MANAGER') && (
        <Card ref={pendingCheckinsRef} elevation={2} sx={{ mb: 6, borderRadius: 2 }}>
          <CardHeader 
            title="Outstanding Check-ins" 
            titleTypographyProps={{ variant: 'h6' }}
            avatar={
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <AssignmentIcon />
              </Avatar>
            }
            action={
              clientsWithPendingCheckins.length > 6 && (
                <Button 
                  size="small" 
                  onClick={() => setIsPendingCheckinsExpanded(!isPendingCheckinsExpanded)}
                  endIcon={isPendingCheckinsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {isPendingCheckinsExpanded ? 'Show Less' : 'Show More'}
                </Button>
              )
            }
          />
          <Divider />
          <CardContent>
            {clientsWithPendingCheckins.length > 0 ? (
              <Box>
                {/* Split clients into two groups */}
                {(() => {
                  const twoWeekCheckIns = clientsWithPendingCheckins.filter(c => c.pending_check_in_type === '2_week');
                  const tenWeekCheckIns = clientsWithPendingCheckins.filter(c => c.pending_check_in_type === '10_week');

                  return (
                    <>
                      {/* Section 1: 2-Week Check-ins */}
                      {twoWeekCheckIns.length > 0 && (
                        <Box sx={{ mb: tenWeekCheckIns.length > 0 ? 4 : 0 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: theme.palette.warning.light,
                          }}>
                            <AccessTimeIcon sx={{ color: theme.palette.warning.dark, fontSize: 24 }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.warning.dark }}>
                              2-Week Check-ins
                            </Typography>
                            <Chip
                              label={twoWeekCheckIns.length}
                              color="warning"
                              size="small"
                              sx={{ fontWeight: 700, minWidth: 35 }}
                            />
                          </Box>

                          {twoWeekCheckIns
                            .slice(0, isPendingCheckinsExpanded ? twoWeekCheckIns.length : 3)
                            .map((client, index, displayedClients) => {
                              const firstSessionDate = toDate(client.first_ignition_session_date || client.first_session_date);
                              const dueDate = firstSessionDate ? addWeeks(firstSessionDate, 2) : null;

                              return (
                                <Paper
                                  key={client.id}
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    mb: index < displayedClients.length - 1 ? 1 : 0,
                                    backgroundColor: theme.palette.warning.light,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    border: `1px solid ${theme.palette.warning.main}`,
                                    '&:hover': {
                                      backgroundColor: theme.palette.warning.main,
                                      '& .MuiTypography-root': {
                                        color: theme.palette.warning.contrastText
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: theme.palette.warning.contrastText
                                      }
                                    }
                                  }}
                                  onClick={() => handleClientClick(client.id)}
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: { xs: 0.5, sm: 2 },
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flex: { xs: 'auto', sm: 1 }
                                    }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                          {client.client_name || 'Client #' + client.client_id}
                                        </Typography>
                                      </Box>
                                      {client.current_programme?.toLowerCase().includes('ignition') && (
                                        <Chip
                                          icon={<WhatshotIcon />}
                                          label="Ignition"
                                          size="small"
                                          color="primary"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            '& .MuiChip-icon': {
                                              fontSize: 14
                                            }
                                          }}
                                        />
                                      )}
                                      {client.current_programme?.toLowerCase().includes('foundation') && (
                                        <Chip
                                          icon={<FitnessCenterIcon />}
                                          label="Foundation"
                                          size="small"
                                          color="secondary"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            '& .MuiChip-icon': {
                                              fontSize: 14
                                            }
                                          }}
                                        />
                                      )}
                                    </Box>

                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      flex: { xs: 'auto', sm: 'none' },
                                      flexWrap: { xs: 'wrap', sm: 'nowrap' }
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                        <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {client.lead_coach_name || 'No lead coach'}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                        <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {(() => {
                                            const location = client.primary_location;
                                            if (!location) return 'No location';
                                            const locationLower = location.toLowerCase();
                                            if (locationLower.includes('cbd')) return 'Club Street';
                                            if (locationLower.includes('one north')) return 'one-north';
                                            return location;
                                          })()}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                                        <AssignmentIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                        <Typography
                                          variant="body2"
                                          color="error"
                                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}
                                        >
                                          Due: {dueDate ? formatSafeDate(dueDate, 'MMM dd') : 'N/A'}
                                        </Typography>
                                      </Box>

                                      <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        startIcon={<RateReviewIcon />}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openDashboardCheckInDialog(client, '2_week');
                                        }}
                                        sx={{
                                          flexShrink: 0,
                                          whiteSpace: 'nowrap',
                                          minWidth: { xs: '100%', sm: 'auto' }
                                        }}
                                      >
                                        Add 2-Week
                                      </Button>
                                    </Box>
                                  </Box>
                                </Paper>
                              );
                            })}
                        </Box>
                      )}

                      {/* Section 2: 10-Week Check-ins */}
                      {tenWeekCheckIns.length > 0 && (
                        <Box>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: theme.palette.info.light,
                          }}>
                            <CheckCircleIcon sx={{ color: theme.palette.info.dark, fontSize: 24 }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.info.dark }}>
                              10-Week Check-ins
                            </Typography>
                            <Chip
                              label={tenWeekCheckIns.length}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700, minWidth: 35 }}
                            />
                          </Box>

                          {tenWeekCheckIns
                            .slice(0, isPendingCheckinsExpanded ? tenWeekCheckIns.length : 3)
                            .map((client, index, displayedClients) => {
                              const firstSessionDate = toDate(client.first_ignition_session_date || client.first_session_date);
                              const dueDate = firstSessionDate ? addWeeks(firstSessionDate, 10) : null;

                              return (
                                <Paper
                                  key={client.id}
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    mb: index < displayedClients.length - 1 ? 1 : 0,
                                    backgroundColor: theme.palette.info.light,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    border: `1px solid ${theme.palette.info.main}`,
                                    '&:hover': {
                                      backgroundColor: theme.palette.info.main,
                                      '& .MuiTypography-root': {
                                        color: theme.palette.info.contrastText
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: theme.palette.info.contrastText
                                      }
                                    }
                                  }}
                                  onClick={() => handleClientClick(client.id)}
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: { xs: 0.5, sm: 2 },
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flex: { xs: 'auto', sm: 1 }
                                    }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                          {client.client_name || 'Client #' + client.client_id}
                                        </Typography>
                                      </Box>
                                      {client.current_programme?.toLowerCase().includes('ignition') && (
                                        <Chip
                                          icon={<WhatshotIcon />}
                                          label="Ignition"
                                          size="small"
                                          color="primary"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            '& .MuiChip-icon': {
                                              fontSize: 14
                                            }
                                          }}
                                        />
                                      )}
                                      {client.current_programme?.toLowerCase().includes('foundation') && (
                                        <Chip
                                          icon={<FitnessCenterIcon />}
                                          label="Foundation"
                                          size="small"
                                          color="secondary"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            '& .MuiChip-icon': {
                                              fontSize: 14
                                            }
                                          }}
                                        />
                                      )}
                                    </Box>

                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      flex: { xs: 'auto', sm: 'none' },
                                      flexWrap: { xs: 'wrap', sm: 'nowrap' }
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                        <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {client.lead_coach_name || 'No lead coach'}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                        <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                        <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {(() => {
                                            const location = client.primary_location;
                                            if (!location) return 'No location';
                                            const locationLower = location.toLowerCase();
                                            if (locationLower.includes('cbd')) return 'Club Street';
                                            if (locationLower.includes('one north')) return 'one-north';
                                            return location;
                                          })()}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                                        <AssignmentIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                          Due: {dueDate ? formatSafeDate(dueDate, 'MMM dd') : 'N/A'}
                                        </Typography>
                                      </Box>

                                      <Button
                                        variant="contained"
                                        color="info"
                                        size="small"
                                        startIcon={<RateReviewIcon />}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openDashboardCheckInDialog(client, '10_week');
                                        }}
                                        sx={{
                                          flexShrink: 0,
                                          whiteSpace: 'nowrap',
                                          minWidth: { xs: '100%', sm: 'auto' }
                                        }}
                                      >
                                        Add 10-Week
                                      </Button>
                                    </Box>
                                  </Box>
                                </Paper>
                              );
                            })}
                        </Box>
                      )}
                    </>
                  );
                })()}

                {/* Old single list code - hidden */}
                {false && clientsWithPendingCheckins
                  .slice(0, isPendingCheckinsExpanded ? clientsWithPendingCheckins.length : 6)
                  .map((client, index, displayedClients) => (
                  <Paper 
                    key={client.id}
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      mb: index < displayedClients.length - 1 ? 2 : 0,
                      backgroundColor: theme.palette.warning.light,
                      borderRadius: 1,
                      cursor: 'pointer',
                      position: 'relative',
                      ...(client.current_programme?.toLowerCase().includes('ignition') && {
                        border: '2px solid',
                        borderColor: 'primary.main',
                        background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.light} 98%, ${theme.palette.primary.light} 100%)`,
                        '&:hover': {
                          borderColor: 'primary.dark',
                        }
                      })
                    }}
                    onClick={() => handleClientClick(client.id)}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1">
                              {client.client_name || 'Client #' + client.client_id}
                            </Typography>
                            {client.current_programme?.toLowerCase().includes('ignition') && (
                              <Chip
                                icon={<WhatshotIcon />}
                                label="Ignition"
                                size="small"
                                color="primary"
                                sx={{ 
                                  height: 24,
                                  '& .MuiChip-icon': { 
                                    fontSize: 16,
                                    color: 'primary.contrastText'
                                  }
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {(() => {
                                const location = client.primary_location;
                                if (!location) return 'No location';
                                const locationLower = location.toLowerCase();
                                if (locationLower.includes('cbd')) return 'Club Street';
                                if (locationLower.includes('one north')) return 'one-north';
                                return location;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {client.lead_coach_name || 'No lead coach'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        {client.pending_check_in_type && (
                          <Typography variant="body2">
                            <strong>Check-in Type:</strong> {client.pending_check_in_type === '2_week' ? '2 Week' : '10 Week'}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          <strong>Last Session:</strong> {client.visit_dates && client.visit_dates.length > 0 
                            ? formatSafeDate(client.visit_dates[client.visit_dates.length - 1], 'MMM dd, yyyy')
                            : 'None'
                          }
                        </Typography>
                        {client.first_ignition_session_date && client.pending_check_in_type && (
                          <Typography variant="body2">
                            <strong>Check-in Due:</strong> {(() => {
                              const sessionDate = toDate(client.first_ignition_session_date);
                              if (!sessionDate) return 'N/A';
                              const dueDate = addWeeks(
                                sessionDate, 
                                client.pending_check_in_type === '2_week' ? 2 : 10
                              );
                              return formatDateSafe(dueDate, 'MMM dd, yyyy');
                            })()}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Programme:</strong> {client.current_programme || 'N/A'}
                          </Typography>
                          {client.current_programme?.toLowerCase().includes('ignition') && (() => {
                            // Extract total PT sessions from programme string (e.g., "Ignition Programme 2025 | 22 PT" -> 22)
                            const match = client.current_programme.match(/\|\s*(\d+)\s*PT/i);
                            const totalSessions = match ? parseInt(match[1]) : 0;
                            const completedSessions = client.pt_sessions || 0;
                            const progressPercentage = totalSessions > 0 ? Math.min((completedSessions / totalSessions) * 100, 100) : 0;
                            
                            return (
                              <Box sx={{ mt: 1.5 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>PT:</strong> {completedSessions}/{totalSessions}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>•</strong> Nutrition: {client.nutrition_sessions || 0}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>•</strong> Metabolic: {client.metabolic_screenings || 0}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>•</strong> Physical: {client.physical_performance_screenings || 0}
                                  </Typography>
                                </Box>
                                {totalSessions > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        PT Progress
                                      </Typography>
                                      <Typography variant="caption" color="primary" fontWeight="medium">
                                        {Math.round(progressPercentage)}%
                                      </Typography>
                                    </Box>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={progressPercentage} 
                                      sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        backgroundColor: 'action.hover',
                                        '& .MuiLinearProgress-bar': {
                                          borderRadius: 4,
                                          background: progressPercentage === 100 
                                            ? 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)'
                                            : 'linear-gradient(45deg, #2196f3 30%, #42a5f5 90%)',
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}

                {(() => {
                  const twoWeekCheckIns = clientsWithPendingCheckins.filter(c => c.pending_check_in_type === '2_week');
                  const tenWeekCheckIns = clientsWithPendingCheckins.filter(c => c.pending_check_in_type === '10_week');
                  const totalShown = isPendingCheckinsExpanded
                    ? clientsWithPendingCheckins.length
                    : Math.min(twoWeekCheckIns.length, 3) + Math.min(tenWeekCheckIns.length, 3);
                  const totalHidden = clientsWithPendingCheckins.length - totalShown;

                  return !isPendingCheckinsExpanded && totalHidden > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsPendingCheckinsExpanded(true)}
                        endIcon={<ExpandMoreIcon />}
                      >
                        Show {totalHidden} More
                      </Button>
                    </Box>
                  );
                })()}
              </Box>
            ) : (
              <Typography variant="body1" align="center" py={4}>
                No outstanding check-ins.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
      
     {/* Success Updates */}
     <Card ref={successUpdatesRef} elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
       <CardHeader 
         title="Success Updates Required" 
         titleTypographyProps={{ variant: 'h6' }}
         avatar={
           <Avatar sx={{ bgcolor: 'success.main' }}>
             <EmojiEventsIcon />
           </Avatar>
         }
         action={
           clientsNeedingSuccessUpdates.length > 6 && (
             <Button 
               size="small" 
               onClick={() => setIsSuccessUpdatesExpanded(!isSuccessUpdatesExpanded)}
               endIcon={isSuccessUpdatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
             >
               {isSuccessUpdatesExpanded ? 'Show Less' : 'Show More'}
             </Button>
           )
         }
       />
       <Divider />
       <CardContent>
         {clientsNeedingSuccessUpdates.length > 0 ? (
           <Box>
            {/* Split clients into two groups */}
            {(() => {
              const awaitingFirstUpdate = clientsNeedingSuccessUpdates.filter(c => c.awaiting_first_success_update === true);
              const overdueWithHistory = clientsNeedingSuccessUpdates.filter(c => !c.awaiting_first_success_update);

              return (
                <>
                  {/* Section 1: Awaiting First Success Update */}
                  {awaitingFirstUpdate.length > 0 && (
                    <Box sx={{ mb: overdueWithHistory.length > 0 ? 4 : 0 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.warning.light,
                      }}>
                        <WarningIcon sx={{ color: theme.palette.warning.dark, fontSize: 24 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.warning.dark }}>
                          Awaiting First Success Update
                        </Typography>
                        <Chip
                          label={awaitingFirstUpdate.length}
                          color="warning"
                          size="small"
                          sx={{ fontWeight: 700, minWidth: 35 }}
                        />
                      </Box>

                      {awaitingFirstUpdate
                        .slice(0, isSuccessUpdatesExpanded ? awaitingFirstUpdate.length : 3)
                        .map((client, index, displayedClients) => {
                          const referenceDateString = client.first_session_date;
                          const referenceDate = referenceDateString ? new Date(referenceDateString) : null;
                          const daysSinceReference = referenceDate
                            ? Math.floor((new Date().getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24))
                            : null;
                          const daysOverdue = daysSinceReference !== null ? Math.max(0, daysSinceReference - 42) : 0;
                          const overdueLabel = daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Due now';
                          const firstSessionLabel = client.first_session_date
                            ? `Started ${formatSafeDate(client.first_session_date, 'MMM dd')}`
                            : '';

                          return (
                            <Paper
                              key={client.id}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mb: index < displayedClients.length - 1 ? 1 : 0,
                                backgroundColor: theme.palette.warning.light,
                                borderRadius: 1,
                                cursor: 'pointer',
                                border: `1px solid ${theme.palette.warning.main}`,
                                '&:hover': {
                                  backgroundColor: theme.palette.warning.main,
                                  '& .MuiTypography-root': {
                                    color: theme.palette.warning.contrastText
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: theme.palette.warning.contrastText
                                  }
                                }
                              }}
                              onClick={() => handleClientClick(client.id)}
                            >
                              <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: { xs: 0.5, sm: 2 },
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  flex: { xs: 'auto', sm: 1 }
                                }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                      {client.client_name || 'Client #' + client.client_id}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    <Chip
                                      label={overdueLabel}
                                      size="small"
                                      color="error"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                </Box>

                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  flex: { xs: 'auto', sm: 'none' }
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                    <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                    <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {client.lead_coach_name || 'No lead coach'}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                    <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                    <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {(() => {
                                        const location = client.primary_location;
                                        if (!location) return 'No location';
                                        const locationLower = location.toLowerCase();
                                        if (locationLower.includes('cbd')) return 'Club Street';
                                        if (locationLower.includes('one north')) return 'one-north';
                                        return location;
                                      })()}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                    <Typography
                                      variant="body2"
                                      color="error"
                                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600 }}
                                    >
                                      {firstSessionLabel}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ flex: '0 0 auto', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                    <Chip
                                      label={overdueLabel}
                                      size="small"
                                      color="error"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          );
                        })}
                    </Box>
                  )}

                  {/* Section 2: Overdue Success Updates (with history) */}
                  {overdueWithHistory.length > 0 && (
                    <Box>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.info.light,
                      }}>
                        <EmojiEventsIcon sx={{ color: theme.palette.info.dark, fontSize: 24 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, color: theme.palette.info.dark }}>
                          Overdue Success Updates
                        </Typography>
                        <Chip
                          label={overdueWithHistory.length}
                          color="info"
                          size="small"
                          sx={{ fontWeight: 700, minWidth: 35 }}
                        />
                      </Box>

                      {overdueWithHistory
                        .slice(0, isSuccessUpdatesExpanded ? overdueWithHistory.length : 3)
                        .map((client, index, displayedClients) => {
                          const referenceDateString = client.last_success_update;
                          const referenceDate = referenceDateString ? new Date(referenceDateString) : null;
                          const daysSinceReference = referenceDate
                            ? Math.floor((new Date().getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24))
                            : null;
                          const daysOverdue = daysSinceReference !== null ? Math.max(0, daysSinceReference - 42) : 0;
                          const overdueLabel = daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Due now';
                          const lastUpdateDisplay = client.last_success_update
                            ? formatSafeDate(client.last_success_update, 'MMM dd')
                            : 'Never';

                          return (
                            <Paper
                              key={client.id}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mb: index < displayedClients.length - 1 ? 1 : 0,
                                backgroundColor: theme.palette.info.light,
                                borderRadius: 1,
                                cursor: 'pointer',
                                border: `1px solid ${theme.palette.info.main}`,
                                '&:hover': {
                                  backgroundColor: theme.palette.info.main,
                                  '& .MuiTypography-root': {
                                    color: theme.palette.info.contrastText
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: theme.palette.info.contrastText
                                  }
                                }
                              }}
                              onClick={() => handleClientClick(client.id)}
                            >
                              <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: { xs: 0.5, sm: 2 },
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  flex: { xs: 'auto', sm: 1 }
                                }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                      {client.client_name || 'Client #' + client.client_id}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    <Chip
                                      label={overdueLabel}
                                      size="small"
                                      color={daysOverdue > 14 ? 'error' : 'warning'}
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                </Box>

                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  flex: { xs: 'auto', sm: 'none' }
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                    <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                    <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {client.lead_coach_name || 'No lead coach'}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                                    <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                                    <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {(() => {
                                        const location = client.primary_location;
                                        if (!location) return 'No location';
                                        const locationLower = location.toLowerCase();
                                        if (locationLower.includes('cbd')) return 'Club Street';
                                        if (locationLower.includes('one north')) return 'one-north';
                                        return location;
                                      })()}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                                    <EmojiEventsIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                    >
                                      Last: {lastUpdateDisplay}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ flex: '0 0 auto', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                    <Chip
                                      label={overdueLabel}
                                      size="small"
                                      color={daysOverdue > 14 ? 'error' : 'warning'}
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          );
                        })}
                    </Box>
                  )}
                </>
              );
            })()}

            {/* Old single list code - keeping structure for Show More button */}
            {false && clientsNeedingSuccessUpdates
              .slice(0, isSuccessUpdatesExpanded ? clientsNeedingSuccessUpdates.length : 6)
              .map((client, index, displayedClients) => {
                const awaitingFirstSuccessUpdate = client.awaiting_first_success_update;
                const referenceDateString = awaitingFirstSuccessUpdate
                  ? client.first_session_date
                  : client.last_success_update;
                const referenceDate = referenceDateString ? new Date(referenceDateString) : null;
                const daysSinceReference = referenceDate
                  ? Math.floor((new Date().getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                const daysOverdue = daysSinceReference !== null ? Math.max(0, daysSinceReference - 42) : 0;
                const overdueLabel = daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Due now';
                const lastUpdateDisplay = awaitingFirstSuccessUpdate
                  ? 'Awaiting first update'
                  : (client.last_success_update
                    ? formatSafeDate(client.last_success_update, 'MMM dd')
                    : 'Never');
                const firstSessionLabel = awaitingFirstSuccessUpdate && client.first_session_date
                  ? `First session ${formatSafeDate(client.first_session_date, 'MMM dd')}`
                  : '';

                return (
                  <Paper 
                   key={client.id}
                   elevation={0} 
                   sx={{ 
                     p: 1.5, 
                     mb: index < displayedClients.length - 1 ? 1 : 0,
                     backgroundColor: theme.palette.success.light,
                     borderRadius: 1,
                     cursor: 'pointer',
                     '&:hover': {
                       backgroundColor: theme.palette.success.main,
                       '& .MuiTypography-root': {
                         color: theme.palette.success.contrastText
                       },
                       '& .MuiSvgIcon-root': {
                         color: theme.palette.success.contrastText
                       }
                     }
                   }}
                   onClick={() => handleClientClick(client.id)}
                 >
                   <Box sx={{ 
                     display: 'flex', 
                     flexDirection: { xs: 'column', sm: 'row' },
                     alignItems: { xs: 'stretch', sm: 'center' }, 
                     gap: { xs: 0.5, sm: 2 },
                     overflow: 'hidden'
                   }}>
                     {/* Top row on mobile: Client Name and Days Overdue */}
                     <Box sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       gap: 2,
                       flex: { xs: 'auto', sm: 1 }
                     }}>
                       {/* Client Name */}
                       <Box sx={{ flex: 1, minWidth: 0 }}>
                         <Typography variant="body2" fontWeight="medium" noWrap>
                           {client.client_name || 'Client #' + client.client_id}
                         </Typography>
                       </Box>
                       
                       {/* Days overdue chip - visible on mobile */}
                       <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                         <Chip 
                           label={overdueLabel}
                           size="small"
                           color={daysOverdue > 14 ? 'error' : 'warning'}
                           sx={{ 
                             height: 20,
                             fontSize: '0.7rem',
                             fontWeight: 'bold'
                           }}
                         />
                       </Box>
                     </Box>
                     
                     {/* Bottom row on mobile: Coach, Location, Last Update */}
                     <Box sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       gap: 2,
                       flex: { xs: 'auto', sm: 'none' }
                     }}>
                       {/* Coach with icon */}
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                         <PersonIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                         <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           {client.lead_coach_name || 'No lead coach'}
                         </Typography>
                       </Box>
                       
                       {/* Location with icon */}
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                         <LocationOnIcon sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }} />
                         <Typography variant="body2" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           {(() => {
                             const location = client.primary_location;
                             if (!location) return 'No location';
                             const locationLower = location.toLowerCase();
                             if (locationLower.includes('cbd')) return 'Club Street';
                             if (locationLower.includes('one north')) return 'one-north';
                             return location;
                           })()}
                         </Typography>
                       </Box>
                       
                       {/* Last Success Update with icon */}
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto', minWidth: 0 }}>
                         <EmojiEventsIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box>
                             <Typography 
                               variant="body2" 
                               color={awaitingFirstSuccessUpdate ? 'error' : 'text.secondary'}
                               sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: awaitingFirstSuccessUpdate ? 600 : 'inherit' }}
                             >
                               {lastUpdateDisplay}
                             </Typography>
                             {firstSessionLabel && (
                               <Typography variant="caption" color="text.secondary">
                                 {firstSessionLabel}
                               </Typography>
                             )}
                           </Box>
                           {awaitingFirstSuccessUpdate && (
                             <Chip
                               label="First update required"
                               size="small"
                               color="error"
                               variant="outlined"
                               sx={{ 
                                 height: 20,
                                 fontSize: '0.7rem',
                                 fontWeight: 'bold'
                               }}
                             />
                           )}
                         </Box>
                       </Box>
                       
                       {/* Days overdue chip - hidden on mobile (shown above) */}
                       <Box sx={{ flex: '0 0 auto', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                         <Chip 
                           label={overdueLabel}
                           size="small"
                           color={daysOverdue > 14 ? 'error' : 'warning'}
                           sx={{ 
                             height: 20,
                             fontSize: '0.7rem',
                             fontWeight: 'bold'
                           }}
                         />
                       </Box>
                     </Box>
                   </Box>
                 </Paper>
                );
              })}
             
             {(() => {
               const awaitingFirstUpdate = clientsNeedingSuccessUpdates.filter(c => c.awaiting_first_success_update === true);
               const overdueWithHistory = clientsNeedingSuccessUpdates.filter(c => !c.awaiting_first_success_update);
               const totalShown = isSuccessUpdatesExpanded
                 ? clientsNeedingSuccessUpdates.length
                 : Math.min(awaitingFirstUpdate.length, 3) + Math.min(overdueWithHistory.length, 3);
               const totalHidden = clientsNeedingSuccessUpdates.length - totalShown;

               return !isSuccessUpdatesExpanded && totalHidden > 0 && (
                 <Box sx={{ textAlign: 'center', mt: 2 }}>
                   <Button
                     variant="outlined"
                     size="small"
                     onClick={() => setIsSuccessUpdatesExpanded(true)}
                     endIcon={<ExpandMoreIcon />}
                   >
                     Show {totalHidden} More
                   </Button>
                 </Box>
               );
             })()}
           </Box>
         ) : (
           <Typography variant="body1" align="center" py={4}>
             No clients currently need success updates.
           </Typography>
         )}
       </CardContent>
     </Card>
      
     
      
      {/* Recent Subscription Cancellations */}
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader 
          title="Recent Subscription Cancellations" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'error.main' }}>
              <CancelIcon />
            </Avatar>
          }
          action={
            recentCancellations.length > 5 && (
              <Button 
                size="small" 
                onClick={() => setIsCancellationsExpanded(!isCancellationsExpanded)}
                endIcon={isCancellationsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isCancellationsExpanded ? 'Show Less' : 'Show More'}
              </Button>
            )
          }
        />
        <Divider />
        <CardContent>
          {recentCancellations.length > 0 ? (
            <Box>
              {recentCancellations.slice(0, isCancellationsExpanded ? recentCancellations.length : 5).map((cancellation, index) => (
                <Paper 
                  key={`cancellation-${cancellation.id}`}
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    mb: 2,
                    backgroundColor: theme.palette.error.light,
                    borderRadius: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/clients/${cancellation.client_id}`)}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle1">
                        {cancellation.client_full_name || 'Client #' + cancellation.client_id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Contract:</strong> {cancellation.contract_name || 'Unknown Contract'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2">
                        <strong>Termination Date:</strong> {cancellation.termination_date ? 
                          formatSafeDate(cancellation.termination_date, 'MMM dd, yyyy') : 'Unknown'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2">
                        <strong>Last Updated:</strong> {cancellation.last_updated ? 
                          formatSafeDate(cancellation.last_updated, 'MMM dd, yyyy') : 'Unknown'}
                      </Typography>
                      <Chip 
                        label="Cancelled" 
                        size="small" 
                        color="error" 
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}

            </Box>
          ) : (
            <Typography variant="body1" align="center" py={4}>
              No recent subscription cancellations.
            </Typography>
          )}
        </CardContent>
      </Card>
      
      {/* Performance Indicators */}
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardHeader 
          title="Performance Indicators" 
          titleTypographyProps={{ variant: 'h6' }}
          avatar={
            <Avatar sx={{ bgcolor: 'info.main' }}>
              <BarChartIcon />
            </Avatar>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <ProgressRing 
                    value={stats.conversionRate} 
                    size={80}
                    thickness={4}
                    color={theme.palette.success.main}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Ignition Conversion Rate
                </Typography>
                <Typography variant="h6">
                  {stats.conversionRate.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <ProgressRing 
                    value={stats.firstCycleConversionRate} 
                    size={80}
                    thickness={4}
                    color={theme.palette.success.main}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  First Cycle Conversion Rate
                </Typography>
                <Typography variant="h6">
                  {stats.firstCycleConversionRate.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <ProgressRing 
                    value={(stats.npsAverage / 5) * 100} 
                    size={80}
                    thickness={4}
                    color={theme.palette.primary.main}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  NPS Score
                </Typography>
                <Typography variant="h6">
                  {stats.npsAverage.toFixed(1)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <ProgressRing 
                    value={(stats.completedSessions / (stats.totalClients * 10)) * 100} 
                    size={80}
                    thickness={4}
                    color={theme.palette.info.main}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Avg. Sessions Per Client
                </Typography>
                <Typography variant="h6">
                  {stats.totalClients > 0 ? (stats.completedSessions / stats.totalClients).toFixed(1) : '0'}
                </Typography>
              </Box>
            </Grid>
            
          </Grid>
        </CardContent>
      </Card>
      
      {/* Staff Session Counts Chart Section */}
      <Card elevation={2} sx={{ borderRadius: 3, mb: 0, overflow: 'hidden' }}>
        <CardHeader 
          title="Sessions per Month" 
          titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          avatar={
            <Avatar sx={{ 
              bgcolor: 'secondary.main',
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`
            }}>
              <BarChartIcon />
            </Avatar>
          }
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />
        <CardContent sx={{ p: 3 }}>
          <SessionsPerMonthChart 
            staffSessionData={staffSessionData}
            isLoading={isLoadingSessionData}
          />
        </CardContent>
      </Card>
      
      {/* Dashboard Check-in Shortcut Dialog */}
      <Dialog
        open={dashboardCheckInDialogOpen}
        onClose={() => {
          if (!isSubmittingDashboardCheckIn) {
            resetDashboardCheckInDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          New {getCheckInTypeLabel(dashboardCheckInType)} Check-in
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Client: {selectedDashboardCheckInClient?.client_name || 'N/A'}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {dashboardCheckInType === '2_week'
                ? 'This check-in is typically conducted 2 weeks after the client starts their program.'
                : 'This check-in is typically conducted 10 weeks after the client starts their program.'}
            </Typography>

            {dashboardCheckInError && (
              <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                {dashboardCheckInError}
              </Alert>
            )}

            <TextField
              label="Check-in Notes"
              multiline
              rows={6}
              value={dashboardCheckInNotes}
              onChange={(event) => {
                setDashboardCheckInNotes(event.target.value);
                if (dashboardCheckInError) {
                  setDashboardCheckInError('');
                }
              }}
              fullWidth
              margin="normal"
              placeholder="Enter detailed notes about the client's progress, challenges, and any adjustments to their program..."
              error={dashboardCheckInNotes.trim() === ''}
              helperText={dashboardCheckInNotes.trim() === '' ? 'Check-in notes are required' : ''}
              disabled={isSubmittingDashboardCheckIn}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetDashboardCheckInDialog}
            disabled={isSubmittingDashboardCheckIn}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDashboardCheckInSubmit}
            disabled={isSubmittingDashboardCheckIn || dashboardCheckInNotes.trim() === ''}
          >
            {isSubmittingDashboardCheckIn ? <InlineLoader size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Alert Dialog */}
      <Dialog
        open={clearAlertDialogOpen}
        onClose={() => {
          setClearAlertDialogOpen(false);
          setClearAlertReason('');
          setSelectedAlertReasons([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clear Client Alerts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Client: {selectedClientForClear?.client_name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select the alert reasons to clear:
            </Typography>
          </Box>
          
          <FormGroup sx={{ mb: 3 }}>
            {selectedClientForClear?.alert_reasons?.map((reason, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedAlertReasons.includes(reason)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAlertReasons([...selectedAlertReasons, reason]);
                      } else {
                        setSelectedAlertReasons(selectedAlertReasons.filter(r => r !== reason));
                      }
                    }}
                  />
                }
                label={reason}
              />
            ))}
          </FormGroup>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for clearing alerts"
            value={clearAlertReason}
            onChange={(e) => setClearAlertReason(e.target.value)}
            placeholder="Please provide a reason for clearing these alerts..."
            helperText="This reason will be recorded and sent to the team"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setClearAlertDialogOpen(false);
              setClearAlertReason('');
              setSelectedAlertReasons([]);
            }}
            disabled={clearingAlert}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleClearAlerts()}
            variant="contained"
            color="primary"
            disabled={clearingAlert || !clearAlertReason.trim() || selectedAlertReasons.length === 0}
          >
            {clearingAlert ? <InlineLoader size={20} /> : 'Clear Selected Alerts'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Foundation Alert Dialog */}
      <Dialog
        open={foundationClearAlertDialogOpen}
        onClose={() => {
          setFoundationClearAlertDialogOpen(false);
          setClearFoundationAlertReason('');
          setSelectedFoundationAlertReasons([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clear Foundation Client Alerts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Client: {selectedFoundationClientForClear?.client_name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select the alert reasons to clear:
            </Typography>
          </Box>
          
          <FormGroup sx={{ mb: 3 }}>
            {selectedFoundationClientForClear?.foundation_alert_reasons?.map((reason, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedFoundationAlertReasons.includes(reason)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFoundationAlertReasons([...selectedFoundationAlertReasons, reason]);
                      } else {
                        setSelectedFoundationAlertReasons(selectedFoundationAlertReasons.filter(r => r !== reason));
                      }
                    }}
                  />
                }
                label={reason}
              />
            ))}
          </FormGroup>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for clearing alerts"
            value={clearFoundationAlertReason}
            onChange={(e) => setClearFoundationAlertReason(e.target.value)}
            placeholder="Please provide a reason for clearing these alerts..."
            helperText="This reason will be recorded and sent to the team"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setFoundationClearAlertDialogOpen(false);
              setClearFoundationAlertReason('');
              setSelectedFoundationAlertReasons([]);
            }}
            disabled={clearingFoundationAlert}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleClearFoundationAlerts()}
            variant="contained"
            color="primary"
            disabled={clearingFoundationAlert || !clearFoundationAlertReason.trim() || selectedFoundationAlertReasons.length === 0}
          >
            {clearingFoundationAlert ? <InlineLoader size={20} /> : 'Clear Selected Alerts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegacyDashboard;
